// src/components/answer/AnswerChart.jsx

import React, { useEffect, useMemo, useRef } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  LabelList,
} from "recharts";
import AnswerText from "./AnswerText";

/**
 * AnswerChart
 *
 * One-file production-grade visual/chart renderer for Lexa / AskLoli answers.
 *
 * Design goals:
 * - Keep a single file, as requested.
 * - Use Recharts for real analytical charts.
 * - Keep custom SVG fallback for geometry, diagrams, math/science curves, and unsupported visuals.
 * - Accept forgiving text instructions from model output.
 * - Avoid black-only chart fallback by applying palette/color mapping consistently.
 * - Support finance, accounting, legal probability, education, science, and general analytics.
 *
 * Supported model markers via AnswerRenderer:
 *   [chart] type=scatter; points=A:10,80|B:30,65|C:70,35; title=Risk vs Success
 *   [graph] type=line; x=Year 1,Year 2; series=A:10,20|B:12,24
 *   [geometry] triangle; color=#2563eb
 *
 * Supported chart types / aliases:
 * - bar, horizontal_bar
 * - column, vertical_bar
 * - grouped_bar, grouped_column
 * - stacked_bar, stacked_column
 * - line, trend, multi_line
 * - area, stacked_area
 * - composed, combo, mixed
 * - pie
 * - donut, doughnut
 * - scatter, scatterplot, scatter_diagram
 * - bubble
 * - histogram, distribution
 * - bell_curve, normal_curve, gaussian, precise_bell_curve
 * - confidence_interval, error_bar
 * - confidence_band, probability_band, line_confidence_band
 * - kde_density, density_curve
 * - roc_curve
 * - precision_recall
 * - violin_plot
 * - radar, spider
 * - waterfall, bridge
 * - boxplot, box_plot, box_whisker
 * - heatmap, matrix
 * - funnel
 * - treemap_basic
 * - gauge, meter
 * - progress
 * - timeline
 * - gantt_basic
 * - candlestick_basic, ohlc
 * - pareto
 * - slope
 * - lollipop
 * - dotplot
 * - true regression trendline overlay for scatter/scatter_line
 *
 * Geometry / diagram / graph previews:
 * - circle, ellipse / oval, square, rectangle, triangle, polygon
 * - angle, right_triangle, coordinate_graph, parabola, sine_wave, cosine_wave
 * - vector, number_line, venn, flowchart_basic
 *
 * Notes:
 * - Some chart types are implemented as custom SVG because Recharts does not natively support them.
 * - This file prioritizes robust rendering, graceful fallback, and broad coverage.
 * - For truly advanced scientific plotting, a Python/Plotly backend would still be better later.
 */

const DEFAULT_PALETTE = [
  "#2563eb", // blue
  "#16a34a", // green
  "#dc2626", // red
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#f97316", // orange
  "#84cc16", // lime
  "#ec4899", // pink
  "#64748b", // slate
  "#14b8a6", // teal
  "#a855f7", // purple
];

const COLOR_NAME_MAP = {
  blue: "#2563eb",
  green: "#16a34a",
  red: "#dc2626",
  yellow: "#eab308",
  amber: "#f59e0b",
  orange: "#f97316",
  purple: "#9333ea",
  violet: "#8b5cf6",
  pink: "#ec4899",
  cyan: "#06b6d4",
  teal: "#14b8a6",
  lime: "#84cc16",
  gray: "#64748b",
  grey: "#64748b",
  black: "#111827",
  slate: "#475569",
};

const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#475569";
const GRID = "rgba(15, 23, 42, 0.12)";
const AXIS = "rgba(15, 23, 42, 0.58)";
const CARD_BG = "#ffffff";
const SOFT_BG = "#f8fafc";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function safeNumber(value, fallback = 0) {
  if (value === null || value === undefined) return fallback;
  const cleaned = String(value).replace(/[$,%]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function toFiniteNumber(value) {
  const n = safeNumber(value, NaN);
  return Number.isFinite(n) ? n : null;
}

function isTruthyFlag(value) {
  const s = String(value ?? "").trim().toLowerCase();
  return ["1", "true", "yes", "y", "on", "line", "connect", "connected"].includes(s);
}

function formatValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? "");
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return n.toLocaleString();
  if (Math.abs(n) > 0 && Math.abs(n) < 1) return n.toFixed(3);
  return String(Number.isInteger(n) ? n : Number(n.toFixed(3)));
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function splitLooseList(value) {
  return String(value || "")
    .split(/[,|]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function splitPipeList(value) {
  return String(value || "")
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseNumberList(value) {
  return splitLooseList(value)
    .map((v) => toFiniteNumber(v))
    .filter((n) => n !== null);
}

function normalizeColor(color, fallback = null) {
  const raw = String(color || "").trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (COLOR_NAME_MAP[lower]) return COLOR_NAME_MAP[lower];
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^rgba?\(/i.test(raw)) return raw;
  if (/^hsla?\(/i.test(raw)) return raw;
  return fallback || raw;
}

function parseColorList(value) {
  return splitLooseList(value)
    .map((c) => normalizeColor(c, null))
    .filter(Boolean);
}

function getSeriesColors(count, visual) {
  const requested = Array.isArray(visual.colors) ? visual.colors : [];
  return Array.from({ length: count }, (_, i) => requested[i] || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]);
}

function parseInstructionMap(raw) {
  const map = {};
  const text = String(raw || "").trim();

  // Accept semicolon lines and newline-separated key/value instructions.
  const parts = text
    .split(/[;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  for (const part of parts) {
    const match = part.match(/^([a-zA-Z_][a-zA-Z0-9_\-\s]*)\s*[:=]\s*(.+)$/);
    if (!match) continue;
    const key = match[1].trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
    const value = match[2].trim();
    map[key] = value;
  }

  return map;
}


const CANONICAL_VISUAL_TYPES = new Set([
  "bar",
  "column",
  "grouped_bar",
  "stacked_bar",
  "line",
  "area",
  "stacked_area",
  "composed",
  "pie",
  "donut",
  "scatter",
  "bubble",
  "histogram",
  "bell_curve",
  "confidence_interval",
  "confidence_band",
  "kde_density",
  "roc_curve",
  "precision_recall",
  "violin_plot",
  "radar",
  "pareto",
  "waterfall",
  "boxplot",
  "heatmap",
  "funnel",
  "gauge",
  "progress",
  "timeline",
  "gantt_basic",
  "candlestick_basic",
  "treemap_basic",
  "slope",
  "lollipop",
  "dotplot",
  "circle",
  "ellipse",
  "square",
  "rectangle",
  "triangle",
  "right_triangle",
  "angle",
  "parabola",
  "coordinate_graph",
  "sine_wave",
  "cosine_wave",
  "number_line",
  "venn",
  "flowchart_basic",
]);

const VISUAL_TYPE_ALIASES = {
  vertical_bar: "column",
  vertical_bars: "column",
  column_chart: "column",
  bar_chart: "bar",
  horizontal_bar: "bar",
  horizontal_bars: "bar",
  grouped_column: "grouped_bar",
  grouped_columns: "grouped_bar",
  grouped_bar_chart: "grouped_bar",
  stacked_column: "stacked_bar",
  stacked_columns: "stacked_bar",
  stacked_bar_chart: "stacked_bar",
  trend: "line",
  multi_line: "line",
  multiline: "line",
  line_chart: "line",
  area_chart: "area",
  stacked_area_chart: "stacked_area",
  doughnut: "donut",
  donut_chart: "donut",
  scatterplot: "scatter",
  scatter_plot: "scatter",
  scatter_diagram: "scatter",
  connected_scatter: "scatter",
  scatter_line: "scatter",
  bubble_chart: "bubble",
  distribution: "histogram",
  frequency: "histogram",
  frequency_distribution: "histogram",
  bell: "bell_curve",
  normal: "bell_curve",
  normal_curve: "bell_curve",
  gaussian: "bell_curve",
  gaussian_curve: "bell_curve",
  precise_bell: "bell_curve",
  precise_bell_curve: "bell_curve",
  normal_distribution: "bell_curve",
  confidence_intervals: "confidence_interval",
  ci: "confidence_interval",
  ci_chart: "confidence_interval",
  error_bar: "confidence_interval",
  error_bars: "confidence_interval",
  errorbar: "confidence_interval",
  confidence_bands: "confidence_band",
  probability_band: "confidence_band",
  probability_bands: "confidence_band",
  line_confidence_band: "confidence_band",
  forecast_band: "confidence_band",
  uncertainty_band: "confidence_band",
  kde: "kde_density",
  density: "kde_density",
  density_curve: "kde_density",
  kernel_density: "kde_density",
  roc: "roc_curve",
  receiver_operating_characteristic: "roc_curve",
  pr_curve: "precision_recall",
  precision_recall_curve: "precision_recall",
  violin: "violin_plot",
  regression: "scatter",
  regression_trendline: "scatter",
  trendline_scatter: "scatter",
  spider: "radar",
  spider_chart: "radar",
  radar_chart: "radar",
  combo: "composed",
  mixed: "composed",
  mixed_chart: "composed",
  bridge: "waterfall",
  bridge_chart: "waterfall",
  waterfall_chart: "waterfall",
  box_plot: "boxplot",
  box_whisker: "boxplot",
  box_and_whisker: "boxplot",
  heat_map: "heatmap",
  matrix: "heatmap",
  funnel_chart: "funnel",
  tree_map: "treemap_basic",
  treemap: "treemap_basic",
  meter: "gauge",
  gauge_chart: "gauge",
  progress_bar: "progress",
  time_line: "timeline",
  gantt: "gantt_basic",
  gantt_chart: "gantt_basic",
  candlestick: "candlestick_basic",
  candle: "candlestick_basic",
  ohlc: "candlestick_basic",
  pareto_chart: "pareto",
  slope_chart: "slope",
  lollipop_chart: "lollipop",
  dot_plot: "dotplot",
  dot_chart: "dotplot",
  coordinate: "coordinate_graph",
  coordinate_plane: "coordinate_graph",
  x_y_graph: "coordinate_graph",
  oval: "ellipse",
  sine: "sine_wave",
  cosine: "cosine_wave",
  flowchart: "flowchart_basic",
};

function normalizeType(type) {
  const token = String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (!token) return "";

  const canonical =
    VISUAL_TYPE_ALIASES[token] ||
    token;

  return CANONICAL_VISUAL_TYPES.has(canonical)
    ? canonical
    : "";
}

function hasStructuredKey(map, keys) {
  return keys.some((key) => {
    const value = map?.[key];

    return (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    );
  });
}

function getBareVisualDirective(raw) {
  const firstSegment = String(raw || "")
    .split(/[;\n]+/)[0]
    .trim();

  if (
    !firstSegment ||
    firstSegment.length > 32 ||
    /[:=]/.test(firstSegment)
  ) {
    return "";
  }

  return normalizeType(firstSegment);
}

function inferEquationVisualType(raw) {
  const text = String(raw || "");

  if (
    /\by\s*=\s*[+-]?\s*x\s*\^\s*2\s*$/i.test(text.trim()) ||
    /\by\s*=\s*[+-]?\s*x²\s*$/i.test(text.trim())
  ) {
    return "parabola";
  }

  if (
    /\by\s*=\s*[+-]?\s*(?:sin|\\sin)\s*\(\s*x\s*\)\s*$/i.test(text.trim())
  ) {
    return "sine_wave";
  }

  if (
    /\by\s*=\s*[+-]?\s*(?:cos|\\cos)\s*\(\s*x\s*\)\s*$/i.test(text.trim())
  ) {
    return "cosine_wave";
  }

  if (/\by\s*=\s*[^;\n]+/i.test(text)) {
    return "coordinate_graph";
  }

  return "";
}

function inferType(raw, map) {
  const explicitType =
    map.type ||
    map.chart ||
    map.graph ||
    map.visual ||
    map.diagram ||
    "";

  if (explicitType) {
    return normalizeType(explicitType);
  }

  // Strong structure-based inference only. Common prose words never decide type.
  if (hasStructuredKey(map, ["candles", "ohlc"])) {
    return "candlestick_basic";
  }

  if (hasStructuredKey(map, ["tasks"])) {
    return "gantt_basic";
  }

  if (hasStructuredKey(map, ["events", "timeline"])) {
    return "timeline";
  }

  if (
    hasStructuredKey(map, ["rows", "y_labels"]) &&
    hasStructuredKey(map, ["cols", "columns", "x_labels"]) &&
    hasStructuredKey(map, ["values", "data"])
  ) {
    return "heatmap";
  }

  if (
    hasStructuredKey(map, ["mean", "mu", "average"]) &&
    hasStructuredKey(map, ["sd", "std", "standard_deviation", "sigma"])
  ) {
    return "bell_curve";
  }

  if (
    hasStructuredKey(map, ["low", "lower", "ci_low"]) &&
    hasStructuredKey(map, ["high", "upper", "ci_high"]) &&
    hasStructuredKey(map, ["mean", "expected", "base", "mid", "values"])
  ) {
    return "confidence_interval";
  }

  if (
    parsePointList(map.points || map.data || "").length >= 2 ||
    parseXYLists(map).length >= 2
  ) {
    return "scatter";
  }

  if (
    hasStructuredKey(map, ["series", "datasets"]) &&
    hasStructuredKey(map, ["labels", "categories", "x", "periods"])
  ) {
    return "line";
  }

  if (
    (
      hasStructuredKey(map, ["labels", "categories", "x"]) &&
      hasStructuredKey(map, ["values", "value", "y", "amounts", "amount"])
    ) ||
    parseLabeledPairs(map.data || map.dataset || "").length >= 1
  ) {
    // Generic chart + explicit category/value data gets a conventional column
    // presentation. This is structure-driven, not a fallback from ordinary text.
    return "column";
  }

  const equationType =
    inferEquationVisualType(raw);

  if (equationType) {
    return equationType;
  }

  const bareDirective =
    getBareVisualDirective(raw);

  if (
    [
      "circle",
      "ellipse",
      "square",
      "rectangle",
      "triangle",
      "right_triangle",
      "angle",
      "parabola",
      "sine_wave",
      "cosine_wave",
      "number_line",
      "venn",
      "flowchart_basic",
    ].includes(bareDirective)
  ) {
    return bareDirective;
  }

  return "";
}

function detectEquation(raw, map) {
  const eq = map.equation || map.eq || map.formula || "";
  if (eq) return eq.trim();
  const m = String(raw || "").match(/\by\s*=\s*([^\n;]+)/i);
  return m ? `y=${m[1].trim()}` : "";
}

function parseLabeledPairs(value) {
  const parts = splitPipeList(value).length ? splitPipeList(value) : splitLooseList(value);
  const pairs = [];

  for (const part of parts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(-?\$?\d+(?:\.\d+)?%?)\s*$/);
    if (m) {
      pairs.push({
        label: m[1].trim(),
        value: safeNumber(m[2], 0),
      });
    }
  }

  return pairs;
}

function parsePointList(value) {
  const parts = splitPipeList(value);
  const points = [];

  for (const part of parts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/);
    if (!m) continue;

    const label = m[1].trim();
    const nums = splitLooseList(m[2]).map((x) => safeNumber(x, NaN)).filter(Number.isFinite);

    if (nums.length >= 2) {
      points.push({
        name: label,
        x: nums[0],
        y: nums[1],
        z: nums[2] ?? 1,
        value: nums[2] ?? nums[1],
      });
    }
  }

  return points;
}

function parseXYLists(map) {
  const xs = parseNumberList(map.x_values || map.xs || map.x_numeric || "");
  const ys = parseNumberList(map.y_values || map.ys || map.y_numeric || "");

  if (xs.length && ys.length) {
    const len = Math.min(xs.length, ys.length);
    return Array.from({ length: len }, (_, i) => ({
      name: `Point ${i + 1}`,
      x: xs[i],
      y: ys[i],
      z: 1,
      value: ys[i],
    }));
  }

  return [];
}

function parseSeries(value) {
  const seriesParts = splitPipeList(value);
  const series = [];

  for (const part of seriesParts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/);
    if (!m) continue;
    series.push({
      name: m[1].trim(),
      values: parseNumberList(m[2]),
    });
  }

  return series;
}

function normalizeCategoryData(map) {
  const pairSource = map.data || map.dataset || map.points || "";
  const pairs = parseLabeledPairs(pairSource);

  if (pairs.length >= 1) {
    return pairs.map((p) => ({
      name: p.label,
      value: p.value,
    }));
  }

  const labels = splitLooseList(map.labels || map.label || map.categories || map.category || map.x || "");
  const values = parseNumberList(map.values || map.value || map.y || map.amounts || map.amount || "");

  if (labels.length && values.length) {
    const len = Math.min(
      labels.length,
      values.length
    );

    return Array.from(
      { length: len },
      (_, i) => ({
        name: labels[i],
        value: values[i],
      })
    );
  }

  if (values.length) {
    return values.map((v, i) => ({
      name: `Item ${i + 1}`,
      value: v,
    }));
  }

  return [];
}

function normalizeMultiSeriesData(map) {
  const labels = splitLooseList(map.labels || map.categories || map.x || map.periods || "");
  const parsedSeries = parseSeries(map.series || map.datasets || "");

  if (!parsedSeries.length) {
    const base = normalizeCategoryData(map);
    return {
      data: base,
      keys: base.length ? ["value"] : [],
      labels: base.map((d) => d.name),
    };
  }

  const maxLen = Math.max(
    labels.length,
    ...parsedSeries.map((s) => s.values.length),
    0
  );

  const data = Array.from({ length: maxLen }, (_, i) => {
    const row = {
      name: labels[i] || `Item ${i + 1}`,
    };

    for (const s of parsedSeries) {
      row[s.name] =
        s.values[i] !== undefined
          ? s.values[i]
          : null;
    }

    return row;
  });

  return {
    data,
    keys: parsedSeries.map((s) => s.name),
    labels: data.map((d) => d.name),
  };
}

function buildHistogramData(values, binCount = 10) {
  const nums = values.map(Number).filter(Number.isFinite);
  if (!nums.length) return [];

  const min = Math.min(...nums);
  const max = Math.max(...nums);

  if (min === max) {
    return [{ name: String(min), range: String(min), count: nums.length, value: nums.length }];
  }

  const bins = clamp(Math.round(binCount || 10), 4, 30);
  const size = (max - min) / bins;

  const result = Array.from({ length: bins }, (_, i) => {
    const start = min + i * size;
    const end = min + (i + 1) * size;
    return {
      name: `${formatValue(start)}-${formatValue(end)}`,
      range: `${formatValue(start)}-${formatValue(end)}`,
      count: 0,
      value: 0,
      start,
      end,
    };
  });

  nums.forEach((n) => {
    const idx = Math.min(Math.floor((n - min) / size), bins - 1);
    result[idx].count += 1;
    result[idx].value = result[idx].count;
  });

  return result;
}

function normalPdf(x, mean, sd) {
  if (!sd || sd <= 0) return 0;
  return (1 / (sd * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mean) / sd) ** 2);
}

function buildBellCurveData(map) {
  const sampleValues =
    parseNumberList(
      map.values ||
      map.data ||
      ""
    );

  const explicitMean =
    toFiniteNumber(
      map.mean ??
      map.mu ??
      map.average
    );

  const explicitSd =
    toFiniteNumber(
      map.sd ??
      map.std ??
      map.standard_deviation ??
      map.sigma
    );

  let mean = explicitMean;
  let sd = explicitSd;

  if (
    (mean === null || sd === null) &&
    sampleValues.length >= 2
  ) {
    const sampleMean =
      sampleValues.reduce(
        (sum, value) => sum + value,
        0
      ) / sampleValues.length;

    const sampleSd =
      standardDeviation(sampleValues);

    if (mean === null) mean = sampleMean;
    if (sd === null) sd = sampleSd;
  }

  if (
    mean === null ||
    sd === null ||
    !Number.isFinite(mean) ||
    !Number.isFinite(sd) ||
    sd <= 0
  ) {
    return [];
  }

  const requestedPoints =
    toFiniteNumber(
      map.points_count ??
      map.count ??
      map.samples
    );

  const points = clamp(
    requestedPoints === null
      ? 121
      : requestedPoints,
    41,
    301
  );

  const explicitMin =
    toFiniteNumber(map.min);

  const explicitMax =
    toFiniteNumber(map.max);

  const min =
    explicitMin === null
      ? mean - 4 * sd
      : explicitMin;

  const max =
    explicitMax === null
      ? mean + 4 * sd
      : explicitMax;

  if (
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    max <= min
  ) {
    return [];
  }

  const step =
    (max - min) /
    (points - 1 || 1);

  const raw = Array.from(
    { length: points },
    (_, i) => {
      const x = min + i * step;
      const density =
        normalPdf(x, mean, sd);

      return {
        name: formatValue(x),
        x,
        density,
      };
    }
  );

  const maxDensity =
    Math.max(
      ...raw.map((d) => d.density)
    ) || 1;

  return raw.map((d) => ({
    ...d,
    y:
      (d.density / maxDensity) *
      100,
    value: d.density,
  }));
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  const weight = idx - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

function buildBoxStats(values) {
  const sorted = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) {
    return { min: 0, q1: 0, median: 0, q3: 0, max: 0, iqr: 0 };
  }

  const q1 = percentile(sorted, 0.25);
  const median = percentile(sorted, 0.5);
  const q3 = percentile(sorted, 0.75);
  const iqr = q3 - q1;

  return {
    min: sorted[0],
    q1,
    median,
    q3,
    max: sorted[sorted.length - 1],
    iqr,
  };
}

function buildWaterfallData(baseData) {
  let running = 0;
  return baseData.map((d, i) => {
    const start = running;
    running += safeNumber(d.value, 0);
    return {
      ...d,
      start,
      end: running,
      delta: safeNumber(d.value, 0),
      value: Math.abs(safeNumber(d.value, 0)),
      fill: safeNumber(d.value, 0) >= 0 ? "#16a34a" : "#dc2626",
      isTotal: i === baseData.length - 1 && /total|ending|final/i.test(d.name),
    };
  });
}

function buildParetoData(baseData) {
  const sorted = [...baseData].sort((a, b) => safeNumber(b.value, 0) - safeNumber(a.value, 0));
  const total = sorted.reduce((sum, d) => sum + Math.max(0, safeNumber(d.value, 0)), 0) || 1;
  let cumulative = 0;

  return sorted.map((d) => {
    cumulative += Math.max(0, safeNumber(d.value, 0));
    return {
      ...d,
      cumulative_percent: (cumulative / total) * 100,
    };
  });
}


function linearRegression(points) {
  const clean = (points || [])
    .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

  if (clean.length < 2) return null;

  const n = clean.length;
  const sumX = clean.reduce((s, p) => s + p.x, 0);
  const sumY = clean.reduce((s, p) => s + p.y, 0);
  const sumXY = clean.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = clean.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const meanY = sumY / n;
  const ssTot = clean.reduce((s, p) => s + (p.y - meanY) ** 2, 0);
  const ssRes = clean.reduce((s, p) => s + (p.y - (slope * p.x + intercept)) ** 2, 0);
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 1;
  const minX = Math.min(...clean.map((p) => p.x));
  const maxX = Math.max(...clean.map((p) => p.x));

  return {
    slope,
    intercept,
    r2,
    line: [
      { name: "Regression start", x: minX, y: slope * minX + intercept, value: slope * minX + intercept },
      { name: "Regression end", x: maxX, y: slope * maxX + intercept, value: slope * maxX + intercept },
    ],
  };
}

function parseRangeData(map) {
  const labels =
    splitLooseList(
      map.labels ||
      map.categories ||
      map.x ||
      map.periods ||
      ""
    );

  const meanVals =
    parseNumberList(
      map.mean ||
      map.expected ||
      map.base ||
      map.mid ||
      map.values ||
      map.value ||
      map.y ||
      ""
    );

  const lowVals =
    parseNumberList(
      map.low ||
      map.lower ||
      map.lower_bound ||
      map.ci_low ||
      map.p10 ||
      ""
    );

  const highVals =
    parseNumberList(
      map.high ||
      map.upper ||
      map.upper_bound ||
      map.ci_high ||
      map.p90 ||
      ""
    );

  const pipeRows =
    splitPipeList(
      map.data ||
      map.points ||
      ""
    );

  if (pipeRows.length) {
    const parsed = pipeRows
      .map((part, i) => {
        const m = part.match(
          /^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/
        );

        if (!m) return null;

        const nums =
          parseNumberList(m[2]);

        if (nums.length < 3) {
          return null;
        }

        const [
          low,
          mean,
          high,
        ] = nums;

        if (high < low) {
          return null;
        }

        return {
          name:
            m[1].trim() ||
            `Item ${i + 1}`,
          low,
          mean,
          high,
          range: high - low,
        };
      })
      .filter(Boolean);

    if (parsed.length) {
      return parsed;
    }
  }

  const len = Math.min(
    meanVals.length,
    lowVals.length,
    highVals.length
  );

  if (!len) return [];

  return Array.from(
    { length: len },
    (_, i) => ({
      name:
        labels[i] ||
        `Item ${i + 1}`,
      low: lowVals[i],
      mean: meanVals[i],
      high: highVals[i],
      range:
        highVals[i] -
        lowVals[i],
    })
  ).filter(
    (d) =>
      Number.isFinite(d.low) &&
      Number.isFinite(d.mean) &&
      Number.isFinite(d.high) &&
      d.high >= d.low
  );
}

function parseBandData(map) {
  const data = parseRangeData(map);
  return data.map((d) => ({
    ...d,
    baseline: d.low,
    band: Math.max(0, d.high - d.low),
    expected: d.mean,
  }));
}

function standardDeviation(values) {
  const nums = values.map(Number).filter(Number.isFinite);
  if (nums.length < 2) return 0;
  const mean = nums.reduce((s, v) => s + v, 0) / nums.length;
  const variance = nums.reduce((s, v) => s + (v - mean) ** 2, 0) / (nums.length - 1);
  return Math.sqrt(variance);
}

function buildKdeData(values, requestedBandwidth, pointCount = 121) {
  const nums = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!nums.length) return [];
  if (nums.length === 1) {
    return [
      { x: nums[0] - 1, y: 0, density: 0, name: formatValue(nums[0] - 1) },
      { x: nums[0], y: 100, density: 1, name: formatValue(nums[0]) },
      { x: nums[0] + 1, y: 0, density: 0, name: formatValue(nums[0] + 1) },
    ];
  }

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const sd = standardDeviation(nums) || (max - min) / 6 || 1;
  const n = nums.length;
  const silverman = 1.06 * sd * Math.pow(n, -1 / 5);
  const bandwidth = Math.max(0.0001, safeNumber(requestedBandwidth, silverman || 1));
  const pad = bandwidth * 3;
  const start = min - pad;
  const end = max + pad;
  const count = clamp(pointCount, 41, 241);
  const step = (end - start) / (count - 1 || 1);

  const raw = Array.from({ length: count }, (_, i) => {
    const x = start + i * step;
    const density = nums.reduce((sum, v) => sum + normalPdf(x, v, bandwidth), 0) / n;
    return { x, density, name: formatValue(x) };
  });
  const maxDensity = Math.max(...raw.map((d) => d.density)) || 1;
  return raw.map((d) => ({ ...d, y: (d.density / maxDensity) * 100, value: d.density }));
}

function parseCurvePoints(map) {
  let data = parsePointList(map.points || map.data || "");
  if (!data.length) data = parseXYLists(map);
  return data
    .map((p, i) => ({ name: p.name || `Point ${i + 1}`, x: Number(p.x), y: Number(p.y), value: Number(p.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);
}

function buildViolinShape(values, width, height, left, right, top, bottom, maxHalfWidth) {
  const kde = buildKdeData(values, null, 121);
  const nums = values.map(Number).filter(Number.isFinite);
  if (!kde.length || !nums.length) return { path: "", stats: buildBoxStats(nums) };

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const stats = buildBoxStats(nums);
  const plotH = height - top - bottom;
  const centerX = (width + left - right) / 2;

  /*
    Do NOT crop the KDE tails. Cropping makes the violin look visibly cut off.
    Instead, include the full KDE domain and scale it into a padded viewBox.
    Actual min/max/median markers still use the real sample values.
  */
  const kdeMin = Math.min(...kde.map((d) => d.x), min);
  const kdeMax = Math.max(...kde.map((d) => d.x), max);
  const domainPad = Math.max((kdeMax - kdeMin || 1) * 0.035, 0.001);
  const domainMin = kdeMin - domainPad;
  const domainMax = kdeMax + domainPad;
  const maxY = Math.max(...kde.map((d) => d.y), 1);
  const safeHalfWidth = Math.min(maxHalfWidth, Math.max(48, (width - left - right) * 0.34));

  const scaleY = (v) => top + plotH - ((v - domainMin) / (domainMax - domainMin || 1)) * plotH;
  const half = (densityScore) => (densityScore / maxY) * safeHalfWidth;

  const rightSide = kde.map((d) => `${centerX + half(d.y)},${scaleY(d.x)}`);
  const leftSide = [...kde].reverse().map((d) => `${centerX - half(d.y)},${scaleY(d.x)}`);
  return {
    path: `M ${rightSide.join(" L ")} L ${leftSide.join(" L ")} Z`,
    stats,
    scaleY,
    centerX,
    domainMin,
    domainMax,
  };
}

function parseHeatmapData(map) {
  // rows=A,B; cols=X,Y; values=10,20|30,40
  const rows = splitLooseList(map.rows || map.y_labels || map.y || "");
  const cols = splitLooseList(map.cols || map.columns || map.x_labels || map.x || "");
  const rowValues = splitPipeList(map.values || map.data || "");

  const matrix = [];

  for (let r = 0; r < rowValues.length; r += 1) {
    const nums = parseNumberList(rowValues[r]);
    matrix.push(nums);
  }

  if (!rows.length && matrix.length) {
    for (let i = 0; i < matrix.length; i += 1) rows.push(`Row ${i + 1}`);
  }

  if (!cols.length && matrix[0]?.length) {
    for (let i = 0; i < matrix[0].length; i += 1) cols.push(`Col ${i + 1}`);
  }

  return { rows, cols, matrix };
}

function parseTimelineData(map) {
  // events=Filed Complaint:Jan 1|Discovery:Feb 15
  const source =
    map.events ||
    map.timeline ||
    map.data ||
    "";

  return splitPipeList(source)
    .map((part, i) => {
      const separator =
        part.indexOf(":");

      if (separator <= 0) {
        return null;
      }

      const label =
        part.slice(0, separator).trim();

      const date =
        part.slice(separator + 1).trim();

      if (!label || !date) {
        return null;
      }

      return {
        name:
          label ||
          `Event ${i + 1}`,
        date,
      };
    })
    .filter(Boolean);
}

function parseGanttData(map) {
  // tasks=Task A:0,5|Task B:4,10
  const source =
    map.tasks ||
    map.data ||
    "";

  return splitPipeList(source)
    .map((part, i) => {
      const separator =
        part.indexOf(":");

      if (separator <= 0) {
        return null;
      }

      const label =
        part.slice(0, separator).trim();

      const nums =
        parseNumberList(
          part.slice(separator + 1)
        );

      if (
        !label ||
        nums.length < 2
      ) {
        return null;
      }

      const start = nums[0];
      const end = nums[1];

      if (
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        end < start
      ) {
        return null;
      }

      return {
        name:
          label ||
          `Task ${i + 1}`,
        start,
        end,
        duration: end - start,
      };
    })
    .filter(Boolean);
}

function parseCandlestickData(map) {
  // candles=Day1:100,110,90,105|Day2:105,120,101,115
  const source =
    map.candles ||
    map.ohlc ||
    map.data ||
    "";

  return splitPipeList(source)
    .map((part, i) => {
      const separator =
        part.indexOf(":");

      if (separator <= 0) {
        return null;
      }

      const label =
        part.slice(0, separator).trim();

      const nums =
        parseNumberList(
          part.slice(separator + 1)
        );

      if (
        !label ||
        nums.length < 4
      ) {
        return null;
      }

      const [
        open,
        high,
        low,
        close,
      ] = nums;

      if (
        ![
          open,
          high,
          low,
          close,
        ].every(Number.isFinite) ||
        high < low ||
        high < Math.max(open, close) ||
        low > Math.min(open, close)
      ) {
        return null;
      }

      return {
        name:
          label ||
          `T${i + 1}`,
        open,
        high,
        low,
        close,
      };
    })
    .filter(Boolean);
}


function estimateCountFromMap(map, keys = []) {
  for (const key of keys) {
    const value = map?.[key];
    if (!value) continue;
    if (String(value).includes("|")) return splitPipeList(value).length;
    const list = splitLooseList(value);
    if (list.length) return list.length;
  }
  return 0;
}

function getRecommendedVisualHeight(type, map = {}) {
  const t = normalizeType(type);
  const explicit = toFiniteNumber(map.height);
  if (explicit !== null) return explicit;

  const labelsCount = estimateCountFromMap(map, ["labels", "categories", "x", "periods"]);
  const rowCount = estimateCountFromMap(map, ["rows", "y_labels", "tasks", "events", "timeline", "data", "points"]);

  switch (t) {
    case "bell_curve":
    case "kde_density":
      return 430;
    case "confidence_band":
    case "scatter":
    case "bubble":
    case "roc_curve":
    case "precision_recall":
      return 420;
    case "violin_plot":
      return 560;
    case "radar":
      return 430;
    case "confidence_interval":
      return clamp(130 + Math.max(labelsCount, rowCount, 3) * 48, 320, 620);
    case "heatmap": {
      const rows = estimateCountFromMap(map, ["rows", "y_labels", "y"]);
      const cols = estimateCountFromMap(map, ["cols", "columns", "x_labels", "x"]);
      return clamp(150 + Math.max(rows || 3, 3) * 46 + Math.max(cols || 3, 3) * 4, 330, 680);
    }
    case "timeline":
      return clamp(130 + Math.max(rowCount, 3) * 52, 300, 720);
    case "gantt_basic":
      return clamp(130 + Math.max(rowCount, 3) * 48, 320, 720);
    case "histogram":
    case "boxplot":
    case "waterfall":
    case "pareto":
      return 410;
    case "pie":
    case "donut":
    case "gauge":
      return 390;
    default:
      return 390;
  }
}


const VISUAL_FILE_EXTENSION_RE =
  /\.(?:pdf|docx?|xlsx?|pptx?|csv|txt|rtf|odt|ods|odp|pages|numbers|key|json|xml|html?|md|zip|png|jpe?g|gif|webp|heic|heif|svg)\b/i;

const VISUAL_PATH_RE =
  /(?:[A-Za-z]:\\(?:[^\\\r\n]+\\)*[^\\\r\n]*|(?:^|\s)\/(?:[^/\s]+\/)+[^/\s]*)/;

const VISUAL_DATE_RE =
  /^(?:\d{4}-\d{1,2}-\d{1,2}(?:[T\s]\d{1,2}:\d{2}(?::\d{2})?)?|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:,\s*\d{4})?)$/i;

function hasFiniteCategoryValue(data) {
  return (data || []).some((row) =>
    Number.isFinite(
      Number(row?.value)
    )
  );
}

function hasFiniteSeriesValue(data, keys) {
  return (data || []).some((row) =>
    (keys || []).some((key) =>
      Number.isFinite(
        Number(row?.[key])
      )
    )
  );
}

function hasExplicitNumericMapValue(map, keys) {
  return keys.some((key) => {
    const value = map?.[key];

    if (
      value === undefined ||
      value === null ||
      String(value).trim() === ""
    ) {
      return false;
    }

    return toFiniteNumber(value) !== null;
  });
}

function parseSlopeData(map) {
  return splitPipeList(
    map.data ||
    map.points ||
    ""
  )
    .map((part, i) => {
      const separator =
        part.indexOf(":");

      if (separator <= 0) {
        return null;
      }

      const label =
        part.slice(0, separator).trim();

      const nums =
        parseNumberList(
          part.slice(separator + 1)
        );

      if (
        !label ||
        nums.length < 2
      ) {
        return null;
      }

      return {
        name:
          label ||
          `Item ${i + 1}`,
        start: nums[0],
        end: nums[1],
      };
    })
    .filter(Boolean);
}

function isSupportedCoordinateEquation(equation) {
  const value = String(
    equation || ""
  )
    .replace(/\s+/g, "")
    .toLowerCase();

  if (!value) return false;

  return (
    /^y=[+-]?(?:\d+(?:\.\d+)?)?x(?:[+-]\d+(?:\.\d+)?)?$/.test(value) ||
    /^y=[+-]?\d+(?:\.\d+)?$/.test(value)
  );
}

function validateHeatmap(map) {
  const {
    rows,
    cols,
    matrix,
  } = parseHeatmapData(map);

  if (
    !rows.length ||
    !cols.length ||
    !matrix.length ||
    matrix.length < rows.length
  ) {
    return false;
  }

  return rows.every(
    (_, rowIndex) =>
      Array.isArray(matrix[rowIndex]) &&
      matrix[rowIndex].length >=
        cols.length &&
      matrix[rowIndex]
        .slice(0, cols.length)
        .every(Number.isFinite)
  );
}

function validateVisualInstruction(visual) {
  const {
    raw,
    map,
    type,
    equation,
  } = visual;

  const text =
    String(raw || "").trim();

  if (!text || !type) {
    return false;
  }

  if (
    VISUAL_FILE_EXTENSION_RE.test(text) ||
    /https?:\/\//i.test(text) ||
    VISUAL_PATH_RE.test(text)
  ) {
    return false;
  }

  const categoryData =
    normalizeCategoryData(map);

  const multi =
    normalizeMultiSeriesData(map);

  let points =
    parsePointList(
      map.points ||
      map.data ||
      ""
    );

  if (!points.length) {
    points = parseXYLists(map);
  }

  const numericValues =
    parseNumberList(
      map.values ||
      map.data ||
      ""
    );

  switch (type) {
    case "bar":
    case "column":
    case "grouped_bar":
    case "stacked_bar":
      return (
        multi.data.length >= 1 &&
        multi.keys.length >= 1 &&
        hasFiniteSeriesValue(
          multi.data,
          multi.keys
        )
      );

    case "line":
    case "area":
    case "stacked_area":
      return (
        multi.data.length >= 2 &&
        multi.keys.length >= 1 &&
        hasFiniteSeriesValue(
          multi.data,
          multi.keys
        )
      );

    case "composed":
      return (
        multi.data.length >= 2 &&
        multi.keys.length >= 2 &&
        hasFiniteSeriesValue(
          multi.data,
          multi.keys
        )
      );

    case "pie":
    case "donut":
      return (
        categoryData.length >= 2 &&
        hasFiniteCategoryValue(
          categoryData
        )
      );

    case "scatter":
    case "bubble":
      return (
        points.length >= 2 &&
        points.every(
          (point) =>
            Number.isFinite(point.x) &&
            Number.isFinite(point.y)
        )
      );

    case "histogram":
    case "boxplot":
    case "kde_density":
    case "violin_plot":
      return numericValues.length >= 2;

    case "bell_curve":
      return (
        buildBellCurveData(map).length >= 2
      );

    case "confidence_interval":
    case "confidence_band":
      return (
        parseRangeData(map).length >= 1
      );

    case "roc_curve":
    case "precision_recall":
      return (
        parseCurvePoints(map).length >= 2
      );

    case "radar":
      return (
        categoryData.length >= 3 &&
        hasFiniteCategoryValue(
          categoryData
        )
      );

    case "pareto":
    case "waterfall":
    case "funnel":
    case "treemap_basic":
    case "lollipop":
    case "dotplot":
      return (
        categoryData.length >= 2 &&
        hasFiniteCategoryValue(
          categoryData
        )
      );

    case "slope":
      return (
        parseSlopeData(map).length >= 1
      );

    case "heatmap":
      return validateHeatmap(map);

    case "gauge":
    case "progress":
      return hasExplicitNumericMapValue(
        map,
        [
          "value",
          "score",
          "percent",
          "progress",
        ]
      );

    case "timeline": {
      const events =
        parseTimelineData(map);

      return (
        events.length >= 1 &&
        events.every((event) =>
          VISUAL_DATE_RE.test(
            event.date
          )
        )
      );
    }

    case "gantt_basic":
      return (
        parseGanttData(map).length >= 1
      );

    case "candlestick_basic":
      return (
        parseCandlestickData(map).length >= 1
      );

    case "circle":
    case "ellipse":
    case "square":
    case "rectangle":
    case "triangle":
    case "right_triangle":
    case "venn":
      return true;

    case "angle":
      return hasExplicitNumericMapValue(
        map,
        ["degrees", "angle"]
      );

    case "parabola":
    case "sine_wave":
    case "cosine_wave":
      return true;

    case "number_line": {
      const linePoints =
        parseNumberList(
          map.points ||
          map.values ||
          ""
        );

      const min =
        toFiniteNumber(map.min);

      const max =
        toFiniteNumber(map.max);

      return (
        linePoints.length >= 1 ||
        (
          min !== null &&
          max !== null &&
          max > min
        )
      );
    }

    case "coordinate_graph":
      return (
        isSupportedCoordinateEquation(
          equation
        ) ||
        points.length >= 2
      );

    case "flowchart_basic":
      return (
        splitPipeList(
          map.steps ||
          map.nodes ||
          map.flow ||
          ""
        ).length >= 2
      );

    default:
      return false;
  }
}

function parseVisualInstruction(value) {
  const raw =
    String(value || "").trim();

  const map =
    parseInstructionMap(raw);

  const type =
    inferType(raw, map);

  const title =
    String(
      map.title ||
      map.name ||
      ""
    ).trim();

  const subtitle =
    String(
      map.subtitle ||
      map.note ||
      ""
    ).trim();

  const colors =
    parseColorList(
      map.colors ||
      map.color_palette ||
      map.palette ||
      map.color ||
      ""
    );

  const equation =
    detectEquation(raw, map);

  const visual = {
    raw,
    map,
    type,
    title,
    subtitle,
    colors,
    equation,
    xLabel:
      map.x_label ||
      map.xlabel ||
      map.x_axis ||
      "",
    yLabel:
      map.y_label ||
      map.ylabel ||
      map.y_axis ||
      "",
    zLabel:
      map.z_label ||
      map.zlabel ||
      map.size_label ||
      "",
    color:
      colors[0] ||
      normalizeColor(
        map.color,
        DEFAULT_PALETTE[0]
      ),
    height: clamp(
      getRecommendedVisualHeight(
        type,
        map
      ),
      260,
      760
    ),
  };

  return {
    ...visual,
    isValid:
      validateVisualInstruction(
        visual
      ),
  };
}

/* =========================
   Layout / UI helpers
   ========================= */

function ChartShell({ title, subtitle, children, height }) {
  const chartHeight = clamp(safeNumber(height, 390), 260, 760);
  const scrollRef = useRef(null);

  /*
    Every chart owns its own horizontal scrolling viewport.

    Important behavior:
    - The viewport stays within the chart card.
    - The immediate white chart canvas grows with the rendered chart.
    - The scrollbar appears only when that specific chart is wider than the viewport.
    - A chart always opens at the far-left position instead of inheriting another
      chart's scroll position.
  */
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollLeft = 0;
  }, [title, subtitle, chartHeight, children]);

  return (
    <div
      className="answer-chart-card"
      style={{
        position: "relative",
        overflow: "hidden",
        marginBottom: 34,
        paddingBottom: 18,
        minHeight: chartHeight + 104,
        maxWidth: "100%",
      }}
    >
      <div className="answer-chart-label">
        {String(title || "Visual").toUpperCase()}
      </div>

      {subtitle ? (
        <div
          className="answer-chart-subtitle"
          style={{ marginBottom: 8, fontSize: 13, color: TEXT_MUTED }}
        >
          {subtitle}
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className="answer-chart-preview"
        role="region"
        aria-label={`${String(title || "Chart")} — horizontally scrollable when needed`}
        tabIndex={0}
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: chartHeight,
          overflowX: "auto",
          overflowY: "hidden",
          overscrollBehaviorX: "contain",
          WebkitOverflowScrolling: "touch",
          scrollbarGutter: "stable",
          position: "relative",
          zIndex: 1,
          paddingBottom: 10,
        }}
      >
        <div
          className="answer-chart-canvas"
          style={{
            display: "inline-block",
            width: "max-content",
            minWidth: "100%",
            maxWidth: "none",
            height: chartHeight,
            minHeight: chartHeight,
            overflow: "visible",
            position: "relative",
            verticalAlign: "top",
            background: CARD_BG,
            borderRadius: 12,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function EmptyChart({ message = "Chart data was not provided in a renderable format." }) {
  return (
    <div
      style={{
        minHeight: 180,
        borderRadius: 14,
        border: `1px dashed ${GRID}`,
        background: SOFT_BG,
        display: "grid",
        placeItems: "center",
        color: TEXT_MUTED,
        fontSize: 14,
        padding: 16,
        textAlign: "center",
      }}
    >
      {message}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const cleanPayload = payload
    .filter((item) => item && item.value !== undefined && item.value !== null)
    .filter((item) => item.type !== "none")
    .slice(0, 12);

  if (!cleanPayload.length) return null;

  const firstRaw = cleanPayload[0]?.payload || {};
  const coordinateParts = [];
  if (Number.isFinite(Number(firstRaw.x))) coordinateParts.push(`x: ${formatValue(firstRaw.x)}`);
  if (Number.isFinite(Number(firstRaw.y))) coordinateParts.push(`y: ${formatValue(firstRaw.y)}`);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(15,23,42,0.14)",
        borderRadius: 12,
        padding: "9px 11px",
        boxShadow: "0 14px 30px rgba(15,23,42,0.14)",
        color: TEXT_DARK,
        fontSize: 12,
        maxWidth: 285,
        minWidth: 150,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        pointerEvents: "none",
      }}
    >
      {label !== undefined && label !== null ? (
        <div style={{ fontWeight: 850, marginBottom: 4 }}>{String(label)}</div>
      ) : null}
      {!label && coordinateParts.length ? (
        <div style={{ fontWeight: 850, marginBottom: 4 }}>{coordinateParts.join(" · ")}</div>
      ) : null}
      {cleanPayload.map((item, i) => {
        const raw = item.payload || {};
        const name = item.name || item.dataKey || raw.name || `Series ${i + 1}`;
        const value = item.value;
        const color =
          raw.__tooltipColor ||
          raw.fill ||
          item.color ||
          item.stroke ||
          item.fill ||
          DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
        const extra = [];
        if (Number.isFinite(Number(raw.low)) && Number.isFinite(Number(raw.high))) {
          extra.push(`range ${formatValue(raw.low)}–${formatValue(raw.high)}`);
        }
        if (Number.isFinite(Number(raw.density))) {
          extra.push(`density ${formatValue(raw.density)}`);
        }
        if (Number.isFinite(Number(raw.x)) && Number.isFinite(Number(raw.y)) && !coordinateParts.length) {
          extra.push(`x ${formatValue(raw.x)}, y ${formatValue(raw.y)}`);
        }
        return (
          <div key={`${name}-${i}`} style={{ display: "grid", gridTemplateColumns: "10px 1fr auto", gap: 7, alignItems: "start", marginTop: i ? 4 : 0 }}>
            <span
              style={{
                width: 9,
                height: 9,
                marginTop: 4,
                borderRadius: 999,
                background: color,
                display: "inline-block",
              }}
            />
            <span style={{ color: TEXT_MUTED, minWidth: 0 }}>{name}</span>
            <strong style={{ color: TEXT_DARK }}>{formatValue(value)}</strong>
            {extra.length ? <span /> : null}
            {extra.length ? <span style={{ gridColumn: "2 / 4", color: TEXT_MUTED, fontSize: 11 }}>{extra.join(" · ")}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

const SAFE_TOOLTIP_PROPS = {
  allowEscapeViewBox: { x: true, y: true },
  wrapperStyle: { zIndex: 1000, pointerEvents: "none" },
  offset: 12,
};


function RechartsFrame({ children, height = 390, width = 760 }) {
  /*
    The frame and the Recharts surface use the same calculated width.
    This keeps the immediate white background aligned with the complete chart,
    including axes, labels, bars, and legends, while ChartShell supplies the
    independent conditional horizontal scrollbar.
  */
  const chartWidth = Math.max(420, safeNumber(width, 760));
  const chartHeight = clamp(safeNumber(height, 390), 260, 760);

  if (!React.isValidElement(children)) {
    return <EmptyChart message="Chart could not be rendered." />;
  }

  return (
    <div
      className="answer-chart-recharts-frame"
      style={{
        width: chartWidth,
        minWidth: chartWidth,
        maxWidth: "none",
        height: chartHeight,
        minHeight: chartHeight,
        overflow: "visible",
        position: "relative",
        zIndex: 2,
        background: CARD_BG,
        borderRadius: 12,
      }}
    >
      {React.cloneElement(children, {
        width: chartWidth,
        height: chartHeight,
      })}
    </div>
  );
}

function getDataKeys(data, fallback = ["value"]) {
  if (!data?.length) return fallback;
  const keys = Object.keys(data[0]).filter((k) => !["name", "label", "range", "fill", "start", "end", "x", "y", "z"].includes(k));
  return keys.length ? keys : fallback;
}


function CategoryValueLegend({ data, colors, valueKey = "value", title = "" }) {
  const rows = (data || [])
    .map((row, index) => ({
      name: row?.name || row?.label || `Item ${index + 1}`,
      value: safeNumber(row?.[valueKey], 0),
      color: colors?.[index % Math.max(colors.length, 1)] || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length],
    }))
    .filter((row) => row.name !== undefined && row.name !== null);

  if (!rows.length) return null;

  return (
    <div
      className="answer-chart-category-legend"
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px 14px",
        maxWidth: "100%",
        padding: "6px 10px 0",
        marginTop: 2,
        color: TEXT_MUTED,
        fontSize: 12,
        lineHeight: 1.25,
      }}
      aria-label={title || "Chart legend"}
    >
      {rows.map((row, index) => (
        <div
          key={`category-legend-${index}-${row.name}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
            maxWidth: 190,
            whiteSpace: "nowrap",
          }}
          title={`${row.name}: ${formatValue(row.value)}`}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: row.color,
              flex: "0 0 auto",
              boxShadow: "0 0 0 1px rgba(15,23,42,0.08)",
            }}
          />
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: TEXT_MUTED,
              fontWeight: 650,
            }}
          >
            {row.name}
          </span>
          <strong style={{ color: TEXT_DARK, fontWeight: 850 }}>{formatValue(row.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function renderCategoryLegendContent(data, colors, valueKey, title) {
  return function CategoryLegendContent() {
    return <CategoryValueLegend data={data} colors={colors} valueKey={valueKey} title={title} />;
  };
}

/* =========================
   Recharts renderers
   ========================= */

function RenderBarLike({ visual, mode = "column", stacked = false, grouped = false }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const layout = mode === "bar" ? "vertical" : "horizontal";
  const isSingleSeries = keys.length === 1 && keys[0] === "value" && !stacked && !grouped;
  const seriesColors = getSeriesColors(keys.length, visual);
  const barColors = getSeriesColors(data.length, visual);
  const chartData = isSingleSeries
    ? data.map((row, index) => ({
        ...row,
        __tooltipColor: barColors[index % barColors.length],
      }))
    : data;
  const maxValue = Math.max(
    1,
    ...data.flatMap((row) => keys.map((key) => Math.abs(safeNumber(row[key], 0))))
  );
  const numericDomain = [0, Math.ceil(maxValue * 1.18)];
  const legendName = visual.map.legend || visual.map.series_name || visual.map.value_label || visual.yLabel || "Amount";
  const showCategoryLegend = isSingleSeries && data.length > 1;
  const categoryLegendContent = renderCategoryLegendContent(data, barColors, keys[0] || "value", visual.title || legendName);

  const commonMargin = layout === "vertical"
    ? { top: 30, right: showCategoryLegend ? 82 : 54, bottom: showCategoryLegend ? 72 : 36, left: 18 }
    : { top: 42, right: 44, bottom: showCategoryLegend ? 82 : 42, left: 18 };

  return (
    <RechartsFrame height={visual.height}>
      <BarChart
        data={chartData}
        layout={layout}
        margin={commonMargin}
        barCategoryGap={layout === "vertical" ? "18%" : "24%"}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        {layout === "vertical" ? (
          <>
            <XAxis type="number" domain={numericDomain} tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={12} tick={false} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} interval={0} />
            <YAxis domain={numericDomain} tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
          </>
        )}
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        {showCategoryLegend ? (
          <Legend
            verticalAlign="bottom"
            align="center"
            content={categoryLegendContent}
            wrapperStyle={{ width: "100%", left: 0, bottom: 0 }}
          />
        ) : (
          <Legend />
        )}
        {keys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            name={isSingleSeries ? legendName : key}
            stackId={stacked ? "stack" : undefined}
            fill={isSingleSeries ? barColors[0] : seriesColors[i]}
            radius={layout === "vertical" ? [0, 8, 8, 0] : [8, 8, 0, 0]}
            isAnimationActive={false}
          >
            {isSingleSeries
              ? chartData.map((_, cellIndex) => (
                  <Cell key={`bar-cell-${cellIndex}`} fill={barColors[cellIndex % barColors.length]} />
                ))
              : null}
            <LabelList
              dataKey={key}
              position={layout === "vertical" ? "right" : "top"}
              formatter={formatValue}
              fill={TEXT_DARK}
              fontSize={12}
              fontWeight={850}
              offset={layout === "vertical" ? 8 : 8}
              clipPath="none"
            />
          </Bar>
        ))}
      </BarChart>
    </RechartsFrame>
  );
}

function normalizeLineData(map) {
  /*
    Robust line chart support.

    Accepts:
      type=line; labels=Jan,Feb,Mar; values=10,20,30
      type=line; x=Jan,Feb,Mar; y=10,20,30
      type=line; points=A:10,92|B:25,80|C:40,65
      type=line; data=Jan:10|Feb:20|Mar:30
      type=line; series=Revenue:10,20,30|Expense:5,8,12
  */

  const pointData = parsePointList(map.points || "");
  if (pointData.length >= 2) {
    return {
      data: pointData.map((p) => ({
        name: p.name,
        x: p.x,
        value: p.y,
        y: p.y,
      })),
      keys: ["value"],
      xMode: "number",
    };
  }

  const xyData = parseXYLists(map);
  if (xyData.length >= 2) {
    return {
      data: xyData.map((p) => ({
        name: p.name,
        x: p.x,
        value: p.y,
        y: p.y,
      })),
      keys: ["value"],
      xMode: "number",
    };
  }

  const { data, keys } = normalizeMultiSeriesData(map);
  return {
    data,
    keys,
    xMode: "category",
  };
}

function RenderLine({ visual }) {
  const { data, keys, xMode } = normalizeLineData(visual.map);
  if (!data.length) return <EmptyChart message="Line charts require labels/values, series, or points data." />;

  const colors = getSeriesColors(keys.length, visual);
  const lineKind = visual.map.line_type || visual.map.curve || visual.map.interpolation || "monotone";

  return (
    <RechartsFrame height={visual.height}>
      <LineChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        {xMode === "number" ? (
          <XAxis
            type="number"
            dataKey="x"
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined}
          />
        ) : (
          <XAxis
            dataKey="name"
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined}
          />
        )}
        <YAxis
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined}
        />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        {keys.map((key, i) => (
          <Line
            key={key}
            type={lineKind}
            dataKey={key}
            name={keys.length === 1 && key === "value" ? visual.title || "Line" : key}
            stroke={colors[i]}
            strokeWidth={3}
            connectNulls
            dot={{ r: 5, fill: colors[i], stroke: "#fff", strokeWidth: 2 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </RechartsFrame>
  );
}

function RenderArea({ visual, stacked = false }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(keys.length, visual);

  return (
    <RechartsFrame height={visual.height}>
      <AreaChart data={data} margin={{ top: 12, right: 46, bottom: 28, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        {keys.map((key, i) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stackId={stacked ? "stack" : undefined}
            stroke={colors[i]}
            fill={colors[i]}
            fillOpacity={0.28}
          />
        ))}
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderPieDonut({ visual, donut = false }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(data.length, visual);

  return (
    <RechartsFrame height={visual.height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={donut ? "48%" : 0}
          outerRadius="78%"
          paddingAngle={donut ? 2 : 1}
          label={(entry) => `${entry.name}: ${formatValue(entry.value)}`}
        >
          {data.map((_, i) => (
            <Cell key={`slice-${i}`} fill={colors[i]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
      </PieChart>
    </RechartsFrame>
  );
}

function RenderScatterBubble({ visual, bubble = false }) {
  let data = parsePointList(visual.map.points || visual.map.data || "");
  if (!data.length) data = parseXYLists(visual.map);
  if (!data.length) return <EmptyChart message="Scatter/bubble charts require points like A:10,80|B:30,60." />;

  const color = visual.color || DEFAULT_PALETTE[0];
  const requestedTrend = String(visual.map.trendline || visual.map.regression || visual.map.show_regression || "").trim().toLowerCase();
  const connectLine =
    isTruthyFlag(visual.map.line) ||
    isTruthyFlag(visual.map.connect) ||
    isTruthyFlag(visual.map.connected) ||
    isTruthyFlag(visual.map.show_line) ||
    requestedTrend === "connect" ||
    requestedTrend === "connected" ||
    normalizeType(visual.map.type) === "connected_scatter" ||
    normalizeType(visual.map.type) === "scatter_line";

  const showRegression =
    requestedTrend === "regression" ||
    requestedTrend === "linear" ||
    requestedTrend === "true" ||
    isTruthyFlag(visual.map.regression) ||
    isTruthyFlag(visual.map.show_regression) ||
    normalizeType(visual.map.chart) === "regression_trendline" ||
    normalizeType(visual.map.type) === "regression_trendline";

  const sortedData = [...data].sort((a, b) => safeNumber(a.x, 0) - safeNumber(b.x, 0));
  const regression = showRegression ? linearRegression(sortedData) : null;

  return (
    <RechartsFrame height={visual.height}>
      <ScatterChart margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis
          type="number"
          dataKey="x"
          name={visual.xLabel || "X"}
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined}
        />
        <YAxis
          type="number"
          dataKey="y"
          name={visual.yLabel || "Y"}
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined}
        />
        {bubble ? <ZAxis type="number" dataKey="z" range={[70, 650]} name={visual.zLabel || "Size"} /> : null}
        <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        <Scatter
          name={visual.title || (bubble ? "Bubble" : connectLine ? "Connected Scatter" : "Scatter")}
          data={sortedData}
          fill={color}
          stroke={color}
          line={connectLine ? { stroke: color, strokeWidth: 3 } : false}
          lineType="joint"
          isAnimationActive={false}
          shape={(props) => {
            const { cx, cy, fill } = props;
            return (
              <circle
                cx={cx}
                cy={cy}
                r={bubble ? 9 : 7}
                fill={fill || color}
                stroke="#ffffff"
                strokeWidth={2}
              />
            );
          }}
        />
        {regression ? (
          <Scatter
            name={`Regression trendline${Number.isFinite(regression.r2) ? ` (R² ${regression.r2.toFixed(2)})` : ""}`}
            data={regression.line}
            fill="transparent"
            stroke={DEFAULT_PALETTE[2]}
            line={{ stroke: DEFAULT_PALETTE[2], strokeWidth: 3, strokeDasharray: "6 4" }}
            lineType="joint"
            legendType="line"
            tooltipType="none"
            isAnimationActive={false}
            shape={() => null}
          />
        ) : null}
      </ScatterChart>
    </RechartsFrame>
  );
}

function RenderHistogram({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const binCount = safeNumber(visual.map.bins || visual.map.bin_count || 10, 10);
  const data = buildHistogramData(values, binCount);

  if (!data.length) return <EmptyChart message="Histogram requires values=10,12,14,15,20..." />;

  return (
    <RechartsFrame height={visual.height}>
      <BarChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="range" tick={{ fill: TEXT_MUTED, fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={55} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Bar dataKey="count" name="Frequency" fill={visual.color || DEFAULT_PALETTE[0]} radius={[6, 6, 0, 0]} />
      </BarChart>
    </RechartsFrame>
  );
}

function RenderBellCurve({ visual }) {
  const data =
    buildBellCurveData(visual.map);

  if (!data.length) {
    return (
      <EmptyChart message="Bell curve requires mean/standard deviation or a numeric sample." />
    );
  }

  const sampleValues =
    parseNumberList(
      visual.map.values ||
      visual.map.data ||
      ""
    );

  const explicitMean =
    toFiniteNumber(
      visual.map.mean ??
      visual.map.mu ??
      visual.map.average
    );

  const explicitSd =
    toFiniteNumber(
      visual.map.sd ??
      visual.map.std ??
      visual.map.standard_deviation ??
      visual.map.sigma
    );

  const mean =
    explicitMean !== null
      ? explicitMean
      : sampleValues.reduce(
          (sum, value) =>
            sum + value,
          0
        ) /
        sampleValues.length;

  const sd =
    explicitSd !== null
      ? explicitSd
      : standardDeviation(
          sampleValues
        );
  const shadeLow = toFiniteNumber(visual.map.low || visual.map.shade_low || visual.map.ci_low || visual.map.confidence_low);
  const shadeHigh = toFiniteNumber(visual.map.high || visual.map.shade_high || visual.map.ci_high || visual.map.confidence_high);
  const showStd = !String(visual.map.show_std || visual.map.standard_deviation_lines || "").trim() || isTruthyFlag(visual.map.show_std || visual.map.standard_deviation_lines || "true");

  return (
    <RechartsFrame height={visual.height}>
      <AreaChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis
          dataKey="x"
          type="number"
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          domain={["dataMin", "dataMax"]}
          label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined}
        />
        <YAxis
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          domain={[0, 105]}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined}
        />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        {shadeLow !== null && shadeHigh !== null ? (
          <ReferenceArea x1={shadeLow} x2={shadeHigh} fill={DEFAULT_PALETTE[1]} fillOpacity={0.16} />
        ) : null}
        <Area
          type="monotone"
          dataKey="y"
          name="Relative density"
          stroke={visual.color || DEFAULT_PALETTE[0]}
          fill={visual.color || DEFAULT_PALETTE[0]}
          fillOpacity={0.25}
          isAnimationActive={false}
        />
        <ReferenceLine x={mean} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Mean", fill: TEXT_MUTED, fontSize: 11 }} />
        {showStd ? [-2, -1, 1, 2].map((k) => (
          <ReferenceLine key={k} x={mean + k * sd} stroke={AXIS} strokeDasharray="2 5" />
        )) : null}
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderConfidenceInterval({ visual }) {
  const data = parseRangeData(visual.map);
  if (!data.length) return <EmptyChart message="Confidence interval charts require labels plus low/mean/high values." />;

  const width = 520;
  const height = Math.max(240, 80 + data.length * 44);
  const left = 112;
  const right = 42;
  const top = 34;
  const bottom = 44;
  const rowH = (height - top - bottom) / data.length;
  const allVals = data.flatMap((d) => [d.low, d.mean, d.high]).filter(Number.isFinite);
  const min = Math.min(...allVals, 0);
  const max = Math.max(...allVals, 1);
  const pad = (max - min || 1) * 0.08;
  const scaleX = (v) => left + ((v - (min - pad)) / ((max + pad) - (min - pad) || 1)) * (width - left - right);
  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Confidence interval chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const x = left + r * (width - left - right);
        return <line key={i} x1={x} y1={top - 8} x2={x} y2={height - bottom + 8} stroke={GRID} />;
      })}
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const xLow = scaleX(d.low);
        const xMean = scaleX(d.mean);
        const xHigh = scaleX(d.high);
        return (
          <g key={`${d.name}-${i}`}>
            <text x={left - 12} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 16)}
            </text>
            <line x1={xLow} y1={y} x2={xHigh} y2={y} stroke={color} strokeWidth="4" strokeLinecap="round" />
            <line x1={xLow} y1={y - 9} x2={xLow} y2={y + 9} stroke={color} strokeWidth="3" />
            <line x1={xHigh} y1={y - 9} x2={xHigh} y2={y + 9} stroke={color} strokeWidth="3" />
            <circle cx={xMean} cy={y} r="7" fill={DEFAULT_PALETTE[2]} stroke="#fff" strokeWidth="2" />
            <text x={xHigh + 8} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.low)}–{formatValue(d.high)}
            </text>
          </g>
        );
      })}
      <line x1={left} y1={height - bottom + 8} x2={width - right} y2={height - bottom + 8} stroke={AXIS} />
      <text x={left} y={height - 16} fontSize="10" fill={TEXT_MUTED}>{formatValue(min)}</text>
      <text x={width - right} y={height - 16} textAnchor="end" fontSize="10" fill={TEXT_MUTED}>{formatValue(max)}</text>
    </SvgFrame>
  );
}

function RenderConfidenceBand({ visual }) {
  const data = parseBandData(visual.map);
  if (!data.length) return <EmptyChart message="Confidence/probability bands require low, expected/mean, and high values." />;

  const color = visual.color || DEFAULT_PALETTE[0];
  const bandColor = visual.colors?.[1] || DEFAULT_PALETTE[0];

  return (
    <RechartsFrame height={visual.height}>
      <ComposedChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        <Area type="monotone" dataKey="baseline" stackId="band" stroke="transparent" fill="transparent" name="Low" isAnimationActive={false} />
        <Area type="monotone" dataKey="band" stackId="band" stroke="transparent" fill={bandColor} fillOpacity={0.22} name="Confidence / Probability Band" isAnimationActive={false} />
        <Line type="monotone" dataKey="expected" name="Expected" stroke={color} strokeWidth={3} dot={{ r: 5, fill: color, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="low" name="Low" stroke={AXIS} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="high" name="High" stroke={AXIS} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
      </ComposedChart>
    </RechartsFrame>
  );
}

function RenderKdeDensity({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const data = buildKdeData(values, visual.map.bandwidth, safeNumber(visual.map.points_count || visual.map.count || 121, 121));
  if (!data.length) return <EmptyChart message="KDE density requires values=10,12,14,20..." />;

  return (
    <RechartsFrame height={visual.height}>
      <AreaChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="x" type="number" domain={["dataMin", "dataMax"]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} />
        <YAxis domain={[0, 105]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Area type="monotone" dataKey="y" name="Relative density" stroke={visual.color || DEFAULT_PALETTE[0]} fill={visual.color || DEFAULT_PALETTE[0]} fillOpacity={0.24} isAnimationActive={false} />
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderRocCurve({ visual }) {
  const data = parseCurvePoints(visual.map);
  if (!data.length) return <EmptyChart message="ROC curve requires points=false_positive_rate,true_positive_rate." />;

  return (
    <RechartsFrame height={visual.height}>
      <LineChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis type="number" dataKey="x" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.xLabel || "False Positive Rate", position: "insideBottom", offset: -10 }} />
        <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.yLabel || "True Positive Rate", angle: -90, position: "insideLeft" }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke={AXIS} strokeDasharray="5 5" />
        <Line type="monotone" dataKey="y" name={visual.title || "ROC Curve"} stroke={visual.color || DEFAULT_PALETTE[0]} strokeWidth={3} dot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </RechartsFrame>
  );
}

function RenderPrecisionRecall({ visual }) {
  const data = parseCurvePoints(visual.map);
  if (!data.length) return <EmptyChart message="Precision-recall requires points=threshold:recall,precision or x/y numeric values." />;

  return (
    <RechartsFrame height={visual.height}>
      <LineChart data={data} margin={{ top: 12, right: 46, bottom: 34, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis type="number" dataKey="x" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.xLabel || "Recall", position: "insideBottom", offset: -10 }} />
        <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.yLabel || "Precision", angle: -90, position: "insideLeft" }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        <Line type="monotone" dataKey="y" name={visual.title || "Precision-Recall Curve"} stroke={visual.color || DEFAULT_PALETTE[0]} strokeWidth={3} dot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </RechartsFrame>
  );
}

function RenderViolinPlot({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  if (!values.length) return <EmptyChart message="Violin plot requires values=..." />;

  const width = 560;
  const height = Math.max(500, clamp(safeNumber(visual.height, 560) - 36, 460, 680));
  const left = 78;
  const right = 58;
  const top = 64;
  const bottom = 86;
  const maxHalfWidth = 118;
  const shape = buildViolinShape(values, width, height, left, right, top, bottom, maxHalfWidth);
  const color = visual.color || DEFAULT_PALETTE[0];
  const stats = shape.stats;
  const scaleY = shape.scaleY || ((v) => v);
  const centerX = shape.centerX || width / 2;

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Violin plot">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + r * (height - top - bottom);
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      <path d={shape.path} fill={color} opacity="0.26" stroke={color} strokeWidth="3" />
      <line x1={centerX} y1={scaleY(stats.min)} x2={centerX} y2={scaleY(stats.max)} stroke={TEXT_DARK} strokeWidth="2" />
      <rect x={centerX - 24} y={scaleY(stats.q3)} width="48" height={Math.max(4, scaleY(stats.q1) - scaleY(stats.q3))} rx="8" fill="#ffffff" stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={centerX - 34} y1={scaleY(stats.median)} x2={centerX + 34} y2={scaleY(stats.median)} stroke={DEFAULT_PALETTE[2]} strokeWidth="3" />
      <text x={centerX} y={height - 22} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">
        {visual.map.label || visual.title || "Distribution"}
      </text>
      <text x={left} y={scaleY(stats.max) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Max {formatValue(stats.max)}</text>
      <text x={left} y={scaleY(stats.median) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Median {formatValue(stats.median)}</text>
      <text x={left} y={scaleY(stats.min) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Min {formatValue(stats.min)}</text>
    </SvgFrame>
  );
}

function RenderRadar({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  return (
    <RechartsFrame height={visual.height}>
      <RadarChart data={data} outerRadius="74%">
        <PolarGrid />
        <PolarAngleAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <PolarRadiusAxis tick={{ fill: TEXT_MUTED, fontSize: 11 }} />
        <Radar
          name={visual.title || "Score"}
          dataKey="value"
          stroke={visual.color || DEFAULT_PALETTE[0]}
          fill={visual.color || DEFAULT_PALETTE[0]}
          fillOpacity={0.45}
        />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
      </RadarChart>
    </RechartsFrame>
  );
}

function RenderComposed({ visual }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(keys.length, visual);
  const lineKeys = splitLooseList(visual.map.line_keys || visual.map.lines || "");
  const barKeys = splitLooseList(visual.map.bar_keys || visual.map.bars || "");

  return (
    <RechartsFrame height={visual.height}>
      <ComposedChart data={data} margin={{ top: 12, right: 46, bottom: 28, left: 14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        {keys.map((key, i) => {
          const shouldLine = lineKeys.includes(key) || (!barKeys.includes(key) && i === keys.length - 1);
          if (shouldLine) {
            return <Line key={key} dataKey={key} stroke={colors[i]} strokeWidth={3} type="monotone" />;
          }
          return <Bar key={key} dataKey={key} fill={colors[i]} radius={[6, 6, 0, 0]} />;
        })}
      </ComposedChart>
    </RechartsFrame>
  );
}

function RenderPareto({ visual }) {
  const base = normalizeCategoryData(visual.map);
  const data = buildParetoData(base);
  if (!data.length) return <EmptyChart />;

  return (
    <RechartsFrame height={visual.height}>
      <ComposedChart data={data} margin={{ top: 12, right: 28, bottom: 12, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <YAxis yAxisId="left" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Legend />
        <Bar yAxisId="left" dataKey="value" fill={DEFAULT_PALETTE[0]} radius={[6, 6, 0, 0]} />
        <Line yAxisId="right" type="monotone" dataKey="cumulative_percent" stroke={DEFAULT_PALETTE[2]} strokeWidth={3} />
      </ComposedChart>
    </RechartsFrame>
  );
}

/* =========================
   Custom SVG renderers
   ========================= */

function SvgFrame({ children, viewBox = "0 0 520 300", label = "Chart" }) {
  const viewBoxParts = String(viewBox || "0 0 520 300")
    .trim()
    .split(/\s+/)
    .map(Number);
  const svgWidth = Number.isFinite(viewBoxParts[2]) ? viewBoxParts[2] : 520;
  const svgHeight = Number.isFinite(viewBoxParts[3]) ? viewBoxParts[3] : 300;

  return (
    <svg
      viewBox={viewBox}
      className="answer-chart-svg"
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
      style={{
        width: svgWidth,
        minWidth: svgWidth,
        maxWidth: "none",
        height: svgHeight,
        minHeight: svgHeight,
        display: "block",
        overflow: "visible",
        background: CARD_BG,
      }}
    >
      {children}
    </svg>
  );
}

function RenderWaterfall({ visual }) {
  const base = normalizeCategoryData(visual.map);
  const data = buildWaterfallData(base);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 48;
  const right = 20;
  const top = 24;
  const bottom = 52;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const allVals = data.flatMap((d) => [d.start, d.end]);
  const min = Math.min(0, ...allVals);
  const max = Math.max(1, ...allVals);
  const scaleY = (v) => top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const barW = Math.max(18, plotW / data.length - 14);

  return (
    <SvgFrame label="Waterfall chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + plotH * r;
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      <line x1={left} y1={scaleY(0)} x2={width - right} y2={scaleY(0)} stroke={AXIS} strokeWidth="2" />
      {data.map((d, i) => {
        const x = left + i * (plotW / data.length) + 8;
        const y1 = scaleY(d.start);
        const y2 = scaleY(d.end);
        const y = Math.min(y1, y2);
        const h = Math.max(3, Math.abs(y2 - y1));
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={barW} height={h} rx="6" fill={d.delta >= 0 ? "#16a34a" : "#dc2626"} />
            <text x={x + barW / 2} y={height - 24} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
              {String(d.name).slice(0, 10)}
            </text>
            <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.end)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderBoxPlot({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const stats = buildBoxStats(values);
  if (!values.length) return <EmptyChart message="Box plot requires values=..." />;

  const width = 520;
  const height = 300;
  const left = 60;
  const right = 40;
  const top = 30;
  const bottom = 46;
  const plotW = width - left - right;
  const centerY = top + (height - top - bottom) / 2;
  const min = stats.min;
  const max = stats.max || 1;
  const scaleX = (v) => left + ((v - min) / (max - min || 1)) * plotW;

  return (
    <SvgFrame label="Box plot">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <line x1={left} y1={centerY} x2={width - right} y2={centerY} stroke={AXIS} strokeWidth="2" />
      <line x1={scaleX(stats.min)} y1={centerY - 34} x2={scaleX(stats.min)} y2={centerY + 34} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.max)} y1={centerY - 34} x2={scaleX(stats.max)} y2={centerY + 34} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.min)} y1={centerY} x2={scaleX(stats.q1)} y2={centerY} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.q3)} y1={centerY} x2={scaleX(stats.max)} y2={centerY} stroke={TEXT_DARK} strokeWidth="2" />
      <rect
        x={scaleX(stats.q1)}
        y={centerY - 42}
        width={Math.max(3, scaleX(stats.q3) - scaleX(stats.q1))}
        height="84"
        rx="10"
        fill="rgba(37,99,235,0.18)"
        stroke={visual.color || DEFAULT_PALETTE[0]}
        strokeWidth="3"
      />
      <line x1={scaleX(stats.median)} y1={centerY - 44} x2={scaleX(stats.median)} y2={centerY + 44} stroke="#dc2626" strokeWidth="3" />
      {[
        ["Min", stats.min],
        ["Q1", stats.q1],
        ["Median", stats.median],
        ["Q3", stats.q3],
        ["Max", stats.max],
      ].map(([label, value], i) => (
        <text key={label} x={scaleX(value)} y={height - 18 - (i % 2) * 14} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
          {label}: {formatValue(value)}
        </text>
      ))}
    </SvgFrame>
  );
}

function RenderHeatmap({ visual }) {
  const { rows, cols, matrix } = parseHeatmapData(visual.map);
  if (!matrix.length) return <EmptyChart message="Heatmap requires rows, cols, and values like values=10,20|30,40." />;

  const flat = matrix.flat().filter(Number.isFinite);
  const min = Math.min(...flat, 0);
  const max = Math.max(...flat, 1);
  const cell = 42;
  const left = 92;
  const top = 48;
  const width = left + cols.length * cell + 24;
  const height = top + rows.length * cell + 34;

  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Heatmap">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {cols.map((c, i) => (
        <text key={c} x={left + i * cell + cell / 2} y={28} textAnchor="middle" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
          {String(c).slice(0, 8)}
        </text>
      ))}
      {rows.map((r, row) => (
        <g key={r}>
          <text x={left - 10} y={top + row * cell + cell / 2 + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
            {String(r).slice(0, 12)}
          </text>
          {cols.map((c, col) => {
            const v =
              matrix[row]?.[col];

            if (!Number.isFinite(v)) {
              return null;
            }

            const opacity = clamp((v - min) / (max - min || 1), 0.08, 1);
            return (
              <g key={`${r}-${c}`}>
                <rect
                  x={left + col * cell}
                  y={top + row * cell}
                  width={cell - 4}
                  height={cell - 4}
                  rx="8"
                  fill={color}
                  opacity={opacity}
                  stroke="rgba(15,23,42,0.08)"
                />
                <text x={left + col * cell + cell / 2 - 2} y={top + row * cell + cell / 2 + 4} textAnchor="middle" fontSize="10" fill="#ffffff" fontWeight="800">
                  {formatValue(v)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </SvgFrame>
  );
}

function RenderFunnel({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 320;
  const top = 26;
  const stepH = 44;
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Funnel chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {data.map((d, i) => {
        const w = 380 * (Math.abs(d.value) / max);
        const x = (width - w) / 2;
        const y = top + i * (stepH + 8);
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={w} height={stepH} rx="12" fill={colors[i]} opacity="0.9" />
            <text x={width / 2} y={y + 27} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="800">
              {d.name}: {formatValue(d.value)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderGauge({ visual }) {
  const rawValue =
    toFiniteNumber(
      visual.map.value ??
      visual.map.score ??
      visual.map.percent
    );

  if (rawValue === null) {
    return (
      <EmptyChart message="Gauge requires an explicit numeric value." />
    );
  }

  const value =
    clamp(rawValue, 0, 100);
  const width = 520;
  const height = 280;
  const cx = 260;
  const cy = 220;
  const r = 145;
  const angle = Math.PI * (1 - value / 100);
  const needleX = cx + (r - 28) * Math.cos(angle);
  const needleY = cy - (r - 28) * Math.sin(angle);

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Gauge chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#e5e7eb" strokeWidth="30" strokeLinecap="round" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`} fill="none" stroke="#dc2626" strokeWidth="30" strokeLinecap="round" />
      <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#16a34a" strokeWidth="30" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={TEXT_DARK} strokeWidth="5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="9" fill={TEXT_DARK} />
      <text x={cx} y={cy - 42} textAnchor="middle" fontSize="34" fill={TEXT_DARK} fontWeight="900">
        {formatValue(value)}%
      </text>
      <text x={cx} y={cy - 16} textAnchor="middle" fontSize="13" fill={TEXT_MUTED} fontWeight="700">
        {visual.map.label || "Score"}
      </text>
    </SvgFrame>
  );
}

function RenderProgress({ visual }) {
  const rawValue =
    toFiniteNumber(
      visual.map.value ??
      visual.map.percent ??
      visual.map.progress
    );

  if (rawValue === null) {
    return (
      <EmptyChart message="Progress visual requires an explicit numeric value." />
    );
  }

  const value =
    clamp(rawValue, 0, 100);
  return (
    <div style={{ padding: 24, height: "100%", display: "grid", alignContent: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: TEXT_DARK, marginBottom: 8 }}>
        {visual.map.label || visual.title || "Progress"} — {formatValue(value)}%
      </div>
      <div style={{ height: 22, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", borderRadius: 999, background: visual.color || DEFAULT_PALETTE[0] }} />
      </div>
    </div>
  );
}

function RenderTimeline({ visual }) {
  const data = parseTimelineData(visual.map);
  if (!data.length) return <EmptyChart message="Timeline requires events=Event:Date|Event:Date." />;

  const width = 520;
  const height = Math.max(220, 80 + data.length * 48);
  const x = 120;

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Timeline">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <line x1={x} y1="40" x2={x} y2={height - 30} stroke={AXIS} strokeWidth="3" />
      {data.map((d, i) => {
        const y = 52 + i * 48;
        return (
          <g key={`${d.name}-${i}`}>
            <circle cx={x} cy={y} r="9" fill={DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]} stroke="#fff" strokeWidth="3" />
            <text x={x + 24} y={y - 4} fontSize="13" fill={TEXT_DARK} fontWeight="800">
              {d.name}
            </text>
            <text x={x + 24} y={y + 14} fontSize="11" fill={TEXT_MUTED}>
              {d.date}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderGantt({ visual }) {
  const data = parseGanttData(visual.map);
  if (!data.length) return <EmptyChart message="Gantt requires tasks=Task A:0,5|Task B:4,10." />;

  const width = 520;
  const height = Math.max(220, 70 + data.length * 42);
  const left = 120;
  const right = 24;
  const top = 38;
  const rowH = 34;
  const min = Math.min(...data.map((d) => d.start), 0);
  const max = Math.max(...data.map((d) => d.end), 1);
  const scaleX = (v) => left + ((v - min) / (max - min || 1)) * (width - left - right);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Gantt chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <line x1={left} y1={height - 28} x2={width - right} y2={height - 28} stroke={AXIS} />
      {data.map((d, i) => {
        const y = top + i * rowH;
        const x = scaleX(d.start);
        const w = Math.max(8, scaleX(d.end) - scaleX(d.start));
        return (
          <g key={d.name}>
            <text x={left - 10} y={y + 16} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 16)}
            </text>
            <rect x={x} y={y} width={w} height="22" rx="8" fill={colors[i]} />
            <text x={x + w + 6} y={y + 15} fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.duration)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderCandlestick({ visual }) {
  const data = parseCandlestickData(visual.map);
  if (!data.length) return <EmptyChart message="Candlestick requires candles=Day1:open,high,low,close|Day2:..." />;

  const width = 520;
  const height = 300;
  const left = 48;
  const right = 24;
  const top = 24;
  const bottom = 44;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const min = Math.min(...data.map((d) => d.low), 0);
  const max = Math.max(...data.map((d) => d.high), 1);
  const scaleY = (v) => top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const step = plotW / data.length;
  const bodyW = Math.max(10, step * 0.5);

  return (
    <SvgFrame label="Candlestick chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + plotH * r;
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      {data.map((d, i) => {
        const x = left + i * step + step / 2;
        const up = d.close >= d.open;
        const color = up ? "#16a34a" : "#dc2626";
        const yOpen = scaleY(d.open);
        const yClose = scaleY(d.close);
        return (
          <g key={d.name}>
            <line x1={x} y1={scaleY(d.high)} x2={x} y2={scaleY(d.low)} stroke={color} strokeWidth="2" />
            <rect x={x - bodyW / 2} y={Math.min(yOpen, yClose)} width={bodyW} height={Math.max(3, Math.abs(yClose - yOpen))} rx="3" fill={color} opacity="0.85" />
            <text x={x} y={height - 18} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
              {String(d.name).slice(0, 6)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderTreemapBasic({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const total = data.reduce((s, d) => s + Math.max(0, safeNumber(d.value, 0)), 0) || 1;
  let x = 20;
  let y = 24;
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame label="Treemap chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {data.map((d, i) => {
        const areaW = Math.max(44, (Math.max(0, d.value) / total) * (width - 40));
        const h = 58;
        if (x + areaW > width - 20) {
          x = 20;
          y += h + 10;
        }
        const rect = { x, y, w: Math.min(areaW, width - x - 20), h };
        x += rect.w + 8;
        return (
          <g key={d.name}>
            <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} rx="10" fill={colors[i]} opacity="0.88" />
            <text x={rect.x + 10} y={rect.y + 24} fontSize="12" fill="#fff" fontWeight="800">
              {String(d.name).slice(0, 14)}
            </text>
            <text x={rect.x + 10} y={rect.y + 43} fontSize="11" fill="#fff">
              {formatValue(d.value)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderSlope({ visual }) {
  // data=A:10,30|B:20,15 where start,end
  const points =
    parseSlopeData(visual.map);

  if (!points.length) {
    return (
      <EmptyChart message="Slope chart requires data=A:10,30|B:20,15." />
    );
  }

  const width = 520;
  const height = 300;
  const leftX = 140;
  const rightX = 380;
  const top = 34;
  const bottom = 50;
  const vals = points.flatMap((p) => [p.start, p.end]);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const scaleY = (v) => top + (height - top - bottom) - ((v - min) / (max - min || 1)) * (height - top - bottom);
  const colors = getSeriesColors(points.length, visual);

  return (
    <SvgFrame label="Slope chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <text x={leftX} y={24} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">Start</text>
      <text x={rightX} y={24} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">End</text>
      {points.map((p, i) => {
        const y1 = scaleY(p.start);
        const y2 = scaleY(p.end);
        return (
          <g key={p.name}>
            <line x1={leftX} y1={y1} x2={rightX} y2={y2} stroke={colors[i]} strokeWidth="3" />
            <circle cx={leftX} cy={y1} r="5" fill={colors[i]} />
            <circle cx={rightX} cy={y2} r="5" fill={colors[i]} />
            <text x={leftX - 8} y={y1 + 4} textAnchor="end" fontSize="11" fill={TEXT_DARK}>{p.name} {formatValue(p.start)}</text>
            <text x={rightX + 8} y={y2 + 4} fontSize="11" fill={TEXT_DARK}>{formatValue(p.end)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderLollipop({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 90;
  const right = 28;
  const top = 24;
  const rowH = Math.min(34, (height - 60) / data.length);
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame label="Lollipop chart">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const x2 = left + (Math.abs(d.value) / max) * (width - left - right);
        return (
          <g key={d.name}>
            <text x={left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 12)}
            </text>
            <line x1={left} y1={y} x2={x2} y2={y} stroke={GRID} strokeWidth="3" />
            <circle cx={x2} cy={y} r="8" fill={color} />
            <text x={x2 + 12} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">{formatValue(d.value)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderDotPlot({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 90;
  const right = 28;
  const top = 24;
  const rowH = Math.min(34, (height - 60) / data.length);
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame label="Dot plot">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const x = left + (Math.abs(d.value) / max) * (width - left - right);
        return (
          <g key={d.name}>
            <text x={left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 12)}
            </text>
            <circle cx={x} cy={y} r="8" fill={colors[i]} />
            <text x={x + 12} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">{formatValue(d.value)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

/* =========================
   Geometry / science diagrams
   ========================= */

function RenderGeometry({ visual }) {
  const type = visual.type;
  const color = visual.color || DEFAULT_PALETTE[0];
  const radius = clamp(safeNumber(visual.map.radius || 54, 54), 16, 90);
  const degrees = safeNumber(visual.map.degrees || visual.map.angle || 45, 45);

  if (type === "circle") {
    return (
      <SvgFrame label="Circle">
        <circle cx="260" cy="150" r={radius} fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
        <line x1="260" y1="150" x2={260 + radius} y2="150" stroke={color} strokeWidth="3" />
        <text x={260 + radius / 2} y="142" fontSize="12" fill={TEXT_MUTED}>r</text>
      </SvgFrame>
    );
  }

  if (type === "ellipse") {
    return (
      <SvgFrame label="Ellipse">
        <ellipse cx="260" cy="150" rx="120" ry="70" fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
      </SvgFrame>
    );
  }

  if (type === "square") {
    return (
      <SvgFrame label="Square">
        <rect x="180" y="70" width="160" height="160" rx="12" fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
      </SvgFrame>
    );
  }

  if (type === "rectangle") {
    return (
      <SvgFrame label="Rectangle">
        <rect x="140" y="90" width="240" height="130" rx="12" fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
      </SvgFrame>
    );
  }

  if (type === "triangle" || type === "right_triangle") {
    const points = type === "right_triangle" ? "150,230 370,230 150,70" : "260,55 390,230 130,230";
    return (
      <SvgFrame label="Triangle">
        <polygon points={points} fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" strokeLinejoin="round" />
        {type === "right_triangle" ? <path d="M150 205 L175 205 L175 230" fill="none" stroke={color} strokeWidth="3" /> : null}
      </SvgFrame>
    );
  }

  if (type === "angle") {
    const rad = (degrees * Math.PI) / 180;
    const cx = 220;
    const cy = 210;
    const x2 = cx + 150 * Math.cos(-rad);
    const y2 = cy + 150 * Math.sin(-rad);
    return (
      <SvgFrame label="Angle">
        <line x1="90" y1={cy} x2={cx} y2={cy} stroke={color} strokeWidth="5" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={x2} y2={y2} stroke={color} strokeWidth="5" strokeLinecap="round" />
        <path d={`M ${cx - 48} ${cy} A 48 48 0 0 0 ${cx + 48 * Math.cos(-rad)} ${cy + 48 * Math.sin(-rad)}`} fill="none" stroke="#f59e0b" strokeWidth="4" />
        <text x={cx + 58} y={cy - 52} fontSize="18" fill={TEXT_DARK} fontWeight="900">{degrees}°</text>
      </SvgFrame>
    );
  }

  if (type === "parabola" || type === "coordinate_graph" || type === "sine_wave" || type === "cosine_wave") {
    return <RenderCoordinateGraph visual={visual} />;
  }

  if (type === "number_line") {
    return <RenderNumberLine visual={visual} />;
  }

  if (type === "venn") {
    return <RenderVenn visual={visual} />;
  }

  if (type === "flowchart_basic") {
    return <RenderFlowchart visual={visual} />;
  }

  return <RenderCoordinateGraph visual={visual} />;
}

function parseLinearEquation(equation) {
  const compact =
    String(equation || "")
      .replace(/\s+/g, "")
      .toLowerCase();

  if (!compact) return null;

  const constant = compact.match(
    /^y=([+-]?\d+(?:\.\d+)?)$/
  );

  if (constant) {
    return {
      slope: 0,
      intercept: Number(
        constant[1]
      ),
    };
  }

  const linear = compact.match(
    /^y=([+-]?)(\d*(?:\.\d+)?)x(?:([+-])(\d+(?:\.\d+)?))?$/
  );

  if (!linear) return null;

  const sign =
    linear[1] === "-"
      ? -1
      : 1;

  const magnitude =
    linear[2]
      ? Number(linear[2])
      : 1;

  const slope =
    sign * magnitude;

  const intercept =
    linear[3] && linear[4]
      ? (
          linear[3] === "-"
            ? -1
            : 1
        ) *
        Number(linear[4])
      : 0;

  if (
    !Number.isFinite(slope) ||
    !Number.isFinite(intercept)
  ) {
    return null;
  }

  return {
    slope,
    intercept,
  };
}

function RenderCoordinateGraph({ visual }) {
  const type = visual.type;
  const color =
    visual.color ||
    DEFAULT_PALETTE[0];

  const width = 520;
  const height = 300;
  const left = 45;
  const right = 25;
  const top = 24;
  const bottom = 36;
  const plotW =
    width - left - right;
  const plotH =
    height - top - bottom;
  const x0 =
    left + plotW / 2;
  const y0 =
    top + plotH / 2;

  let path = "";

  if (type === "parabola") {
    const pts = [];

    for (let i = 0; i <= 100; i += 1) {
      const t =
        -2.5 + (5 * i) / 100;

      const x =
        x0 + t * 60;

      const y =
        y0 + 80 - t * t * 28;

      pts.push(
        `${i === 0 ? "M" : "L"} ${x} ${y}`
      );
    }

    path = pts.join(" ");
  } else if (
    type === "sine_wave" ||
    type === "cosine_wave"
  ) {
    const pts = [];

    for (let i = 0; i <= 160; i += 1) {
      const t =
        (Math.PI * 4 * i) / 160;

      const x =
        left +
        (plotW * i) / 160;

      const y =
        y0 -
        (
          type === "sine_wave"
            ? Math.sin(t)
            : Math.cos(t)
        ) *
          70;

      pts.push(
        `${i === 0 ? "M" : "L"} ${x} ${y}`
      );
    }

    path = pts.join(" ");
  } else {
    let points =
      parsePointList(
        visual.map.points ||
        visual.map.data ||
        ""
      );

    if (!points.length) {
      points =
        parseXYLists(visual.map);
    }

    if (points.length >= 2) {
      const sorted =
        [...points].sort(
          (a, b) =>
            a.x - b.x
        );

      const xs =
        sorted.map((p) => p.x);

      const ys =
        sorted.map((p) => p.y);

      const minX =
        Math.min(...xs);
      const maxX =
        Math.max(...xs);
      const minY =
        Math.min(...ys);
      const maxY =
        Math.max(...ys);

      const xPad =
        Math.max(
          1,
          (maxX - minX) * 0.1
        );

      const yPad =
        Math.max(
          1,
          (maxY - minY) * 0.1
        );

      const sx = (value) =>
        left +
        (
          (value - (minX - xPad)) /
          (
            maxX -
            minX +
            xPad * 2 ||
            1
          )
        ) *
          plotW;

      const sy = (value) =>
        top +
        plotH -
        (
          (value - (minY - yPad)) /
          (
            maxY -
            minY +
            yPad * 2 ||
            1
          )
        ) *
          plotH;

      path = sorted
        .map(
          (point, index) =>
            `${
              index === 0
                ? "M"
                : "L"
            } ${sx(point.x)} ${sy(point.y)}`
        )
        .join(" ");
    } else {
      const linear =
        parseLinearEquation(
          visual.equation
        );

      if (!linear) {
        return (
          <EmptyChart message="Coordinate graph requires supported y=mx+b notation or numeric points." />
        );
      }

      const xA = -5;
      const xB = 5;

      const yA =
        linear.slope * xA +
        linear.intercept;

      const yB =
        linear.slope * xB +
        linear.intercept;

      const scaleX = (value) =>
        x0 + value * 38;

      const maxAbsY =
        Math.max(
          5,
          Math.abs(yA),
          Math.abs(yB)
        );

      const scaleY = (value) =>
        y0 -
        (
          value / maxAbsY
        ) *
          (plotH * 0.42);

      path =
        `M ${scaleX(xA)} ${scaleY(yA)} ` +
        `L ${scaleX(xB)} ${scaleY(yB)}`;
    }
  }

  return (
    <SvgFrame label="Coordinate graph">
      <rect
        x="0"
        y="0"
        width={width}
        height={height}
        fill={CARD_BG}
      />

      {Array.from({ length: 7 }, (_, i) => {
        const x =
          left +
          (plotW * i) / 6;

        return (
          <line
            key={`x-${i}`}
            x1={x}
            y1={top}
            x2={x}
            y2={height - bottom}
            stroke={GRID}
          />
        );
      })}

      {Array.from({ length: 5 }, (_, i) => {
        const y =
          top +
          (plotH * i) / 4;

        return (
          <line
            key={`y-${i}`}
            x1={left}
            y1={y}
            x2={width - right}
            y2={y}
            stroke={GRID}
          />
        );
      })}

      <line
        x1={left}
        y1={y0}
        x2={width - right}
        y2={y0}
        stroke={AXIS}
        strokeWidth="2"
      />

      <line
        x1={x0}
        y1={top}
        x2={x0}
        y2={height - bottom}
        stroke={AXIS}
        strokeWidth="2"
      />

      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {visual.equation ? (
        <text
          x={left + 12}
          y={top + 18}
          fontSize="13"
          fill={TEXT_DARK}
          fontWeight="800"
        >
          {visual.equation}
        </text>
      ) : null}
    </SvgFrame>
  );
}

function RenderNumberLine({ visual }) {
  const points =
    parseNumberList(
      visual.map.points ||
      visual.map.values ||
      ""
    );

  const explicitMin =
    toFiniteNumber(
      visual.map.min
    );

  const explicitMax =
    toFiniteNumber(
      visual.map.max
    );

  const derivedMin =
    points.length
      ? Math.min(...points)
      : null;

  const derivedMax =
    points.length
      ? Math.max(...points)
      : null;

  const spread =
    derivedMin !== null &&
    derivedMax !== null
      ? Math.max(
          1,
          derivedMax - derivedMin
        )
      : 1;

  const min =
    explicitMin !== null
      ? explicitMin
      : derivedMin !== null
      ? derivedMin - spread * 0.2
      : 0;

  const max =
    explicitMax !== null
      ? explicitMax
      : derivedMax !== null
      ? derivedMax + spread * 0.2
      : 1;
  const width = 520;
  const height = 180;
  const left = 46;
  const right = 46;
  const y = 90;
  const scale = (v) => left + ((v - min) / (max - min || 1)) * (width - left - right);

  return (
    <SvgFrame viewBox={`0 0 ${width} ${height}`} label="Number line">
      <rect x="0" y="0" width={width} height={height} fill={CARD_BG} />
      <line x1={left} y1={y} x2={width - right} y2={y} stroke={AXIS} strokeWidth="3" />
      <polygon points={`${width - right},${y} ${width - right - 10},${y - 6} ${width - right - 10},${y + 6}`} fill={AXIS} />
      {Array.from({ length: 9 }, (_, i) => {
        const v = min + ((max - min) * i) / 8;
        const x = scale(v);
        return (
          <g key={i}>
            <line x1={x} y1={y - 8} x2={x} y2={y + 8} stroke={AXIS} />
            <text x={x} y={y + 28} textAnchor="middle" fontSize="11" fill={TEXT_MUTED}>{formatValue(v)}</text>
          </g>
        );
      })}
      {points.map((p, i) => (
        <circle key={i} cx={scale(p)} cy={y} r="8" fill={DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]} stroke="#fff" strokeWidth="2" />
      ))}
    </SvgFrame>
  );
}

function RenderVenn({ visual }) {
  return (
    <SvgFrame label="Venn diagram">
      <circle cx="220" cy="150" r="90" fill="rgba(37,99,235,0.28)" stroke="#2563eb" strokeWidth="4" />
      <circle cx="300" cy="150" r="90" fill="rgba(22,163,74,0.28)" stroke="#16a34a" strokeWidth="4" />
      <text x="185" y="150" fontSize="18" fill={TEXT_DARK} fontWeight="900">A</text>
      <text x="335" y="150" fontSize="18" fill={TEXT_DARK} fontWeight="900">B</text>
      <text x="260" y="154" textAnchor="middle" fontSize="16" fill={TEXT_DARK} fontWeight="900">A∩B</text>
    </SvgFrame>
  );
}

function RenderFlowchart({ visual }) {
  const steps =
    splitPipeList(
      visual.map.steps ||
      visual.map.nodes ||
      visual.map.flow ||
      ""
    );

  if (steps.length < 2) {
    return (
      <EmptyChart message="Flowchart requires steps=Step 1|Step 2|Step 3." />
    );
  }

  const width = 520;
  const nodeWidth = 230;
  const nodeHeight = 46;
  const gap = 38;
  const top = 28;

  const height =
    top * 2 +
    steps.length * nodeHeight +
    (steps.length - 1) * gap;

  return (
    <SvgFrame
      viewBox={`0 0 ${width} ${height}`}
      label="Flowchart"
    >
      <rect
        x="0"
        y="0"
        width={width}
        height={height}
        fill={CARD_BG}
      />

      <defs>
        <marker
          id="answer-chart-flow-arrow"
          markerWidth="10"
          markerHeight="10"
          refX="8"
          refY="3"
          orient="auto"
        >
          <path
            d="M0,0 L0,6 L9,3 z"
            fill={AXIS}
          />
        </marker>
      </defs>

      {steps.map((step, index) => {
        const x =
          (width - nodeWidth) / 2;

        const y =
          top +
          index *
            (nodeHeight + gap);

        const nextY =
          y +
          nodeHeight +
          gap;

        const color =
          DEFAULT_PALETTE[
            index %
              DEFAULT_PALETTE.length
          ];

        return (
          <g key={`flow-step-${index}`}>
            <rect
              x={x}
              y={y}
              width={nodeWidth}
              height={nodeHeight}
              rx="14"
              fill={color}
              opacity="0.14"
              stroke={color}
              strokeWidth="3"
            />

            <text
              x={width / 2}
              y={y + 28}
              textAnchor="middle"
              fontSize="13"
              fill={TEXT_DARK}
              fontWeight="800"
            >
              {String(step).slice(0, 42)}
            </text>

            {index < steps.length - 1 ? (
              <line
                x1={width / 2}
                y1={y + nodeHeight}
                x2={width / 2}
                y2={nextY - 8}
                stroke={AXIS}
                strokeWidth="3"
                markerEnd="url(#answer-chart-flow-arrow)"
              />
            ) : null}
          </g>
        );
      })}
    </SvgFrame>
  );
}

/* =========================
   Router
   ========================= */

function renderVisualPreview(visual) {
  switch (visual.type) {
    case "bar":
      return <RenderBarLike visual={visual} mode="bar" />;
    case "column":
      return <RenderBarLike visual={visual} mode="column" />;
    case "grouped_bar":
      return <RenderBarLike visual={visual} mode="column" grouped />;
    case "stacked_bar":
      return <RenderBarLike visual={visual} mode="column" stacked />;
    case "line":
      return <RenderLine visual={visual} />;
    case "area":
      return <RenderArea visual={visual} />;
    case "stacked_area":
      return <RenderArea visual={visual} stacked />;
    case "pie":
      return <RenderPieDonut visual={visual} />;
    case "donut":
      return <RenderPieDonut visual={visual} donut />;
    case "scatter":
      return <RenderScatterBubble visual={visual} />;
    case "bubble":
      return <RenderScatterBubble visual={visual} bubble />;
    case "histogram":
      return <RenderHistogram visual={visual} />;
    case "bell_curve":
      return <RenderBellCurve visual={visual} />;
    case "confidence_interval":
      return <RenderConfidenceInterval visual={visual} />;
    case "confidence_band":
      return <RenderConfidenceBand visual={visual} />;
    case "kde_density":
      return <RenderKdeDensity visual={visual} />;
    case "roc_curve":
      return <RenderRocCurve visual={visual} />;
    case "precision_recall":
      return <RenderPrecisionRecall visual={visual} />;
    case "violin_plot":
      return <RenderViolinPlot visual={visual} />;
    case "radar":
      return <RenderRadar visual={visual} />;
    case "composed":
      return <RenderComposed visual={visual} />;
    case "pareto":
      return <RenderPareto visual={visual} />;
    case "waterfall":
      return <RenderWaterfall visual={visual} />;
    case "boxplot":
      return <RenderBoxPlot visual={visual} />;
    case "heatmap":
      return <RenderHeatmap visual={visual} />;
    case "funnel":
      return <RenderFunnel visual={visual} />;
    case "gauge":
      return <RenderGauge visual={visual} />;
    case "progress":
      return <RenderProgress visual={visual} />;
    case "timeline":
      return <RenderTimeline visual={visual} />;
    case "gantt_basic":
      return <RenderGantt visual={visual} />;
    case "candlestick_basic":
      return <RenderCandlestick visual={visual} />;
    case "treemap_basic":
      return <RenderTreemapBasic visual={visual} />;
    case "slope":
      return <RenderSlope visual={visual} />;
    case "lollipop":
      return <RenderLollipop visual={visual} />;
    case "dotplot":
      return <RenderDotPlot visual={visual} />;
    case "circle":
    case "ellipse":
    case "square":
    case "rectangle":
    case "triangle":
    case "right_triangle":
    case "angle":
    case "parabola":
    case "coordinate_graph":
    case "sine_wave":
    case "cosine_wave":
    case "number_line":
    case "venn":
    case "flowchart_basic":
      return <RenderGeometry visual={visual} />;
    default:
      return (
        <EmptyChart message="This visual instruction is not supported in a safe renderable form." />
      );
  }
}

export default function AnswerChart({ value }) {
  const visual = useMemo(() => parseVisualInstruction(value), [value]);

  if (!visual.raw) return null;

  /*
    Defense in depth:
    AnswerRenderer is the primary router. AnswerChart independently refuses
    prose, filenames, unknown types, incomplete data, and unsupported visual
    instructions instead of manufacturing a plausible-looking chart.
  */
  if (!visual.isValid) {
    return (
      <AnswerText
        value={visual.raw}
      />
    );
  }

  const labelMap = {
    bar: "Bar Chart",
    column: "Column Chart",
    grouped_bar: "Grouped Bar Chart",
    stacked_bar: "Stacked Bar Chart",
    line: "Line Chart",
    area: "Area Chart",
    stacked_area: "Stacked Area Chart",
    pie: "Pie Chart",
    donut: "Donut Chart",
    scatter: "Scatter Diagram",
    bubble: "Bubble Chart",
    histogram: "Histogram",
    bell_curve: "Bell Curve",
    confidence_interval: "Confidence Interval / Error Bar",
    confidence_band: "Confidence / Probability Band",
    kde_density: "KDE Density Curve",
    roc_curve: "ROC Curve",
    precision_recall: "Precision-Recall Curve",
    violin_plot: "Violin Plot",
    radar: "Radar Chart",
    composed: "Composed Chart",
    pareto: "Pareto Chart",
    waterfall: "Waterfall Chart",
    boxplot: "Box Plot",
    heatmap: "Heatmap",
    funnel: "Funnel Chart",
    gauge: "Gauge",
    progress: "Progress",
    timeline: "Timeline",
    gantt_basic: "Gantt Chart",
    candlestick_basic: "Candlestick Chart",
    treemap_basic: "Treemap",
    slope: "Slope Chart",
    lollipop: "Lollipop Chart",
    dotplot: "Dot Plot",
    circle: "Circle",
    ellipse: "Ellipse",
    square: "Square",
    rectangle: "Rectangle",
    triangle: "Triangle",
    right_triangle: "Right Triangle",
    angle: "Angle",
    parabola: "Parabola",
    coordinate_graph: "Coordinate Graph",
    sine_wave: "Sine Wave",
    cosine_wave: "Cosine Wave",
    number_line: "Number Line",
    venn: "Venn Diagram",
    flowchart_basic: "Flowchart",
  };

  const title = visual.title || labelMap[visual.type] || "Visual";
  const subtitle = visual.subtitle || (visual.equation ? `Equation: ${visual.equation}` : "");

  return (
    <ChartShell title={title} subtitle={subtitle} height={visual.height}>
      {renderVisualPreview(visual)}
    </ChartShell>
  );
}
